/**
 * WorkflowDiscoveryPageV2 — proper v2 redesign (G158).
 *
 * Mirrors the v1 WorkflowDiscoveryPage's full functional surface but ports
 * every section to the canonical v2 design system. Five top-level TabNav
 * sections — the same five v1 had — each rendered with v2 components
 * (FacetRail, Table, Slideout, MetricCard, Pagination, EmptyState):
 *
 *   1. Overview  — KPI tiles + GEL pipeline summary + recent activity
 *   2. Sources   — workflow connectors (CRUD + test + discover scan)
 *   3. Workflows — inventory with facets + slideout governance/exec tabs
 *   4. Mapper    — multi-stage Visual Mapper deep-link (delegates to v1
 *                  for the canvas materialization flow; that wizard is
 *                  its own 1.5k-line subsystem that will get its own v2
 *                  redesign separately — too large to inline here)
 *   5. Audit     — bindings table + chain-integrity controls
 *
 * Every backend code path from v1 is preserved verbatim:
 *   - api.discoveryListWorkflows / GetWorkflowSummary / GelGetSummary
 *   - api.discoveryListConnectorConfigs / CreateConnectorConfig
 *   - api.discoveryTestConnector / TriggerDiscover / ReconcileConfirm
 *   - api.discoveryProtectWorkflow / GelAdvancePipeline / GelActivatePipeline
 *   - api.discoveryStartExternalWorkflow
 *   - api.discoveryListBindings / UpdateBindingMode
 *   - api.discoveryGetAuditDashboard / RepairAuditChain / RepairAllAuditChains
 *   - api.discoveryMapper* (analyze/map/materialize/commit/sourceJson/reset/execute/status)
 *
 * v1 stays as fallback via /workflow-discovery?legacy=1 (and is the only
 * surface for the full Visual Mapper wizard until that gets ported).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { isRebirthDiscoveryEnabled } from '../utils/featureFlags';
import { usePersistedState } from '../hooks/usePersistedState';
import * as api from '../services/api';
import EntityLabel from '../components/common/EntityLabel';
import {
  RefreshCw, Plus, Activity, ArrowRight, Shield, Search, Trash2, Play, Cable,
  Workflow, FileText, ShieldCheck, ShieldAlert, ChevronRight,
  RotateCw, ScanSearch, ClipboardList, Eye,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TabNav,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import WorkflowDiscoveryPage from './WorkflowDiscoveryPage';

/* ── Constants mirroring v1 ───────────────────────────────────────── */

const TABS = [
  { id: 'overview',  label: 'Overview' },
  { id: 'sources',   label: 'Sources' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'mapper',    label: 'Visual Mapper' },
  { id: 'audit',     label: 'Audit' },
];

const WF_STATUS_OPTIONS = [
  { key: 'DISCOVERED',  label: 'Discovered',  tone: 'cyan' },
  { key: 'ANALYZING',   label: 'Analyzing',   tone: 'warning' },
  { key: 'MAPPED',      label: 'Mapped',      tone: 'trust' },
  { key: 'PROTECTED',   label: 'Protected',   tone: 'success' },
  { key: 'REGISTERING', label: 'Registering', tone: 'warning' },
  { key: 'ORPHANED',    label: 'Orphaned',    tone: 'danger' },
  { key: 'IGNORED',     label: 'Ignored',     tone: 'muted' },
];
const WF_STATUS_TONE = Object.fromEntries(WF_STATUS_OPTIONS.map(o => [o.key, o.tone]));

const RISK_OPTIONS = [
  { key: 'critical', label: 'Critical', tone: 'danger' },
  { key: 'high',     label: 'High',     tone: 'warning' },
  { key: 'medium',   label: 'Medium',   tone: 'cyan' },
  { key: 'low',      label: 'Low',      tone: 'muted' },
];
const RISK_TONE = Object.fromEntries(RISK_OPTIONS.map(o => [o.key, o.tone]));

const CONN_STATUS_OPTIONS = [
  { key: 'CONNECTED', label: 'Connected', tone: 'success' },
  { key: 'PENDING',   label: 'Pending',   tone: 'warning' },
  { key: 'ERROR',     label: 'Error',     tone: 'danger' },
  { key: 'DISABLED',  label: 'Disabled',  tone: 'muted' },
];
const CONN_STATUS_TONE = Object.fromEntries(CONN_STATUS_OPTIONS.map(o => [o.key, o.tone]));

const ENFORCEMENT_MODES = ['MONITOR', 'WARN', 'ENFORCE', 'BLOCK'];
const ENF_MODE_TONE = { MONITOR: 'cyan', WARN: 'warning', ENFORCE: 'danger', BLOCK: 'danger' };

const GEL_PIPELINE_STEPS = [
  'INITIATED', 'CLASSIFYING', 'RULES_GENERATED', 'BINDING', 'TESTING', 'ACTIVE',
];

/* ── Helpers ──────────────────────────────────────────────────────── */

function fmtRelative(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

/* Robust list-unwrap (G157 lesson): handle Spring Page + ad-hoc shapes
 * + apiFetch wrapper without ever returning a non-array. */
function unwrapList(raw) {
  if (Array.isArray(raw)) return raw;
  const d = raw?.data ?? raw;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.content)) return d.content;
  if (Array.isArray(d?.items))   return d.items;
  if (Array.isArray(d?.workflows)) return d.workflows;
  if (Array.isArray(d?.connectors)) return d.connectors;
  if (Array.isArray(d?.bindings))  return d.bindings;
  return [];
}

/* ── Outer page shell ─────────────────────────────────────────────── */

function WorkflowDiscoveryPageV2Inner() {
  const navigate = useNavigate();
  const toastCtx = useToast();
  /* G152 defensive wrapper — toast call sites that never crash. */
  const addToast = useCallback((arg, type, dur) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') return toastCtx.addToast(arg, type, dur);
    // eslint-disable-next-line no-console
    console.warn('[WorkflowDiscovery] toast unavailable:', arg);
    return null;
  }, [toastCtx]);

  const [tab, setTab] = usePersistedState('wfd.tab', 'workflows');

  /* Cross-tab state — fetched once, drives all five tabs. */
  const [summary, setSummary]       = useState(null);
  const [workflows, setWorkflows]   = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [bindings, setBindings]     = useState([]);
  const [gelSummary, setGelSummary] = useState(null);
  const [auditDash, setAuditDash]   = useState(null);
  const [loading, setLoading]       = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, wfRes, connRes, bindRes, gelRes, auditRes] = await Promise.all([
        api.discoveryGetWorkflowSummary?.()?.catch(() => null),
        api.discoveryListWorkflows?.({ size: 500 })?.catch(() => null),
        api.discoveryListConnectorConfigs?.()?.catch(() => null),
        api.discoveryListBindings?.()?.catch(() => null),
        api.discoveryGelGetSummary?.()?.catch(() => null),
        api.discoveryGetAuditDashboard?.()?.catch(() => null),
      ]);
      setSummary(sum?.data || sum || null);
      setWorkflows(unwrapList(wfRes));
      setConnectors(unwrapList(connRes));
      setBindings(unwrapList(bindRes));
      setGelSummary(gelRes?.data || gelRes || null);
      setAuditDash(auditRes?.data || auditRes || null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  /* Header KPIs — derived once for the Overview tab + tab badges. */
  const kpis = useMemo(() => {
    const total = workflows.length;
    const protectedCount = workflows.filter(w => w.status === 'PROTECTED').length;
    const unprotected   = total - protectedCount;
    return {
      connectors: connectors.length,
      total,
      protected: protectedCount,
      unprotected,
    };
  }, [workflows, connectors]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1600px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Workflow Discovery</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Discover external workflows (Temporal OSS, Conductor, n8n, custom), map them to native CEGs, and
            bring them under GEL protection — protection, enforcement modes, hash-chained audit.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        </div>
      </header>

      <TabNav tabs={TABS} activeId={tab} onChange={setTab} ariaLabel="Workflow Discovery sections" />
      <div className="my-4" />

      {tab === 'overview'  && <OverviewTab kpis={kpis} workflows={workflows} connectors={connectors} gelSummary={gelSummary} setTab={setTab} loading={loading} />}
      {tab === 'sources'   && <SourcesTab connectors={connectors} addToast={addToast} reload={reload} />}
      {tab === 'workflows' && <WorkflowsTab workflows={workflows} connectors={connectors} bindings={bindings} addToast={addToast} reload={reload} navigate={navigate} loading={loading} />}
      {tab === 'mapper'    && <MapperTab workflows={workflows} connectors={connectors} addToast={addToast} navigate={navigate} reload={reload} />}
      {tab === 'audit'     && <AuditTab bindings={bindings} auditDash={auditDash} addToast={addToast} reload={reload} loading={loading} />}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Tab 1: Overview
 * ────────────────────────────────────────────────────────────────── */

function OverviewTab({ kpis, workflows, connectors, gelSummary, setTab, loading }) {
  const activePipelines = useMemo(() => {
    const arr = Array.isArray(gelSummary?.activePipelines) ? gelSummary.activePipelines
              : Array.isArray(gelSummary?.active) ? gelSummary.active
              : [];
    return arr.slice(0, 6);
  }, [gelSummary]);

  const recentlyDiscovered = useMemo(
    () => [...workflows]
      .filter(w => w.firstDiscoveredAt || w.createdAt)
      .sort((a, b) => new Date(b.firstDiscoveredAt || b.createdAt).getTime() - new Date(a.firstDiscoveredAt || a.createdAt).getTime())
      .slice(0, 8),
    [workflows],
  );

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Workflow connectors" value={kpis.connectors} tone="action" />
        <MetricCard label="Workflows discovered" value={kpis.total} tone="cyan" />
        <MetricCard label="Protected" value={kpis.protected}
          tone={kpis.protected > 0 ? 'success' : 'muted'} />
        <MetricCard label="Unprotected" value={kpis.unprotected}
          tone={kpis.unprotected > 0 ? 'warning' : 'success'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card>
          <Card.Header title={`Active GEL pipelines · ${activePipelines.length}`} actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => setTab('workflows')}>
              Open workflows
            </Button>
          } />
          <Card.Body>
            {activePipelines.length === 0 ? (
              <p className="text-[12.5px] text-v2-ink-muted">No GEL pipelines in flight.</p>
            ) : (
              <div className="space-y-2">
                {activePipelines.map((p, i) => {
                  const stepIdx = GEL_PIPELINE_STEPS.indexOf(p.currentStep || p.step);
                  const pct = stepIdx >= 0 ? Math.round(((stepIdx + 1) / GEL_PIPELINE_STEPS.length) * 100) : 0;
                  return (
                    <div key={p.id || i} className="border border-v2-border-subtle rounded-v2-sm p-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[12.5px] font-semibold text-v2-ink truncate">{p.workflowName || p.workflowId || `Pipeline ${i+1}`}</span>
                        <Pill tone="warning" size="sm">{p.currentStep || p.step || 'pending'}</Pill>
                      </div>
                      <div className="h-1 bg-v2-muted rounded-full overflow-hidden">
                        <div className="h-full bg-v2-action" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title={`Recently discovered · ${recentlyDiscovered.length}`} actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => setTab('workflows')}>
              Full inventory
            </Button>
          } />
          <Card.Body>
            {recentlyDiscovered.length === 0 ? (
              <p className="text-[12.5px] text-v2-ink-muted">{loading ? 'Loading…' : 'No external workflows discovered yet — run a scan on the Sources tab.'}</p>
            ) : (
              <ul className="space-y-1.5">
                {recentlyDiscovered.map((w) => (
                  <li key={w.id} className="flex items-center gap-2 text-[12.5px]">
                    <Pill tone={WF_STATUS_TONE[w.status] || 'muted'} size="sm" dot>
                      {(w.status || 'unknown').toLowerCase()}
                    </Pill>
                    <span className="font-semibold text-v2-ink truncate">{w.workflowType || w.name}</span>
                    <span className="text-[10.5px] text-v2-ink-muted ml-auto font-mono-v2">
                      {fmtRelative(w.firstDiscoveredAt || w.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card.Body>
        </Card>

        <Card className="lg:col-span-2">
          <Card.Header title={`Connector health · ${connectors.length}`} actions={
            <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setTab('sources')}>
              Manage sources
            </Button>
          } />
          <Card.Body>
            {connectors.length === 0 ? (
              <EmptyState icon={<Cable size={20} />} title="No workflow connectors configured"
                description="Connect a Temporal OSS cluster, Conductor server, or custom workflow engine to start discovering external workflows."
                primaryAction={<Button variant="primary" onClick={() => setTab('sources')}>Add a connector</Button>} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {connectors.slice(0, 6).map((c) => (
                  <div key={c.id} className="border border-v2-border-subtle rounded-v2-sm p-2.5">
                    <div className="flex items-center gap-2">
                      <Pill tone={CONN_STATUS_TONE[c.status] || 'muted'} size="sm" dot>{(c.status || 'unknown').toLowerCase()}</Pill>
                      <span className="text-[12.5px] font-semibold text-v2-ink truncate">{c.name}</span>
                    </div>
                    <div className="text-[10.5px] text-v2-ink-muted mt-1 font-mono-v2 truncate">
                      {c.connectorType || c.type || 'UNKNOWN'} · {c.host || '—'}:{c.port || '—'}
                    </div>
                    <div className="text-[10.5px] text-v2-ink-muted mt-0.5">
                      {(c.workflowsDiscovered || 0).toLocaleString()} workflow(s)
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Tab 2: Sources (Connectors)
 * ────────────────────────────────────────────────────────────────── */

function SourcesTab({ connectors, addToast, reload }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', connectorType: 'TEMPORAL', host: '', port: '', namespace: '', authType: 'NONE', tlsEnabled: false, scanIntervalMinutes: 60, autoScanEnabled: false });
  const [creating, setCreating]   = useState(false);
  const [testing, setTesting]     = useState({});
  const [discovering, setDiscovering] = useState({});
  const [reconcileDrift, setReconcileDrift] = useState(null);

  const openCreate = () => {
    setForm({ name: '', connectorType: 'TEMPORAL', host: '', port: '', namespace: '', authType: 'NONE', tlsEnabled: false, scanIntervalMinutes: 60, autoScanEnabled: false });
    setShowCreate(true);
  };

  const submitCreate = async () => {
    if (!form.name.trim() || !form.host.trim()) {
      addToast({ tone: 'danger', title: 'Name and host required' }); return;
    }
    setCreating(true);
    try {
      const payload = { ...form, port: parseInt(form.port, 10) || null, scanIntervalMinutes: parseInt(form.scanIntervalMinutes, 10) || 60 };
      const res = await api.discoveryCreateConnectorConfig?.(payload);
      if (res?.ok === false) {
        addToast({ tone: 'danger', title: 'Create failed', description: res?.error || res?.message || 'Backend rejected request' });
        return;
      }
      addToast({ tone: 'success', title: 'Connector added', description: form.name });
      setShowCreate(false);
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Create failed', description: err?.message });
    } finally {
      setCreating(false);
    }
  };

  const testConnector = async (c) => {
    setTesting(t => ({ ...t, [c.id]: true }));
    try {
      const res = await api.discoveryTestConnector?.(c.id);
      const data = res?.data || res;
      if (data?.status === 'CONNECTED' || data?.ok) {
        addToast({ tone: 'success', title: 'Connector reachable', description: `${c.name} · ${data?.latencyMs || '—'}ms` });
      } else {
        addToast({ tone: 'warning', title: 'Connector unhealthy', description: data?.message || data?.error || 'Connection failed' });
      }
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Test failed', description: err?.message });
    } finally {
      setTesting(t => ({ ...t, [c.id]: false }));
    }
  };

  const discoverFor = async (c) => {
    setDiscovering(d => ({ ...d, [c.id]: true }));
    try {
      const res = await api.discoveryTriggerDiscover?.(c.id);
      const data = res?.data || res;
      addToast({ tone: 'success', title: 'Discovery complete',
        description: `${data?.totalDiscovered ?? data?.discoveredCount ?? 0} workflow(s) found.` });
      if (Array.isArray(data?.orphanCandidates) && data.orphanCandidates.length > 0) {
        setReconcileDrift({ connectorId: c.id, connectorName: c.name, orphans: data.orphanCandidates });
      } else {
        reload();
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Discovery failed', description: err?.message });
    } finally {
      setDiscovering(d => ({ ...d, [c.id]: false }));
    }
  };

  /* G466 — Connector delete uses V2 confirm dialog (no native window.confirm). */
  const [deleteConnectorTarget, setDeleteConnectorTarget] = useState(null);
  const deleteConnector = (c) => setDeleteConnectorTarget(c);
  const confirmDeleteConnector = async () => {
    const c = deleteConnectorTarget;
    if (!c) return;
    setDeleteConnectorTarget(null);
    try {
      await api.apiFetch?.(`/api/v1/discovery/connectors/configs/${c.id}`, 'DELETE');
      addToast({ tone: 'info', title: 'Connector deleted',
        description: `"${c.name}" removed. Existing discovered workflows remain.` });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Delete failed', description: err?.message });
    }
  };

  const confirmReconcile = async (orphanIds) => {
    if (!reconcileDrift) return;
    try {
      await api.discoveryReconcileConfirm?.(reconcileDrift.connectorId, orphanIds);
      addToast({ tone: 'success', title: 'Orphans tombstoned', description: `${orphanIds.length} workflow(s) marked.` });
      setReconcileDrift(null);
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Reconcile failed', description: err?.message });
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total" value={connectors.length} tone="cyan" />
        <MetricCard label="Connected" value={connectors.filter(c => c.status === 'CONNECTED').length} tone="success" />
        <MetricCard label="In error" value={connectors.filter(c => c.status === 'ERROR').length}
          tone={connectors.filter(c => c.status === 'ERROR').length > 0 ? 'danger' : 'success'} />
        <MetricCard label="Auto-scan on" value={connectors.filter(c => c.autoScanEnabled).length} tone="action" />
      </div>

      <div className="flex items-center justify-end mb-4">
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={openCreate}>Add connector</Button>
      </div>

      <Card>
        <Card.Body>
          {connectors.length === 0 ? (
            <EmptyState icon={<Cable size={20} />} title="No workflow sources configured"
              description="Add a Temporal OSS, Conductor, n8n, or custom workflow engine connector to begin discovery."
              primaryAction={<Button variant="primary" onClick={openCreate}>Add connector</Button>} />
          ) : (
            <Table
              ariaLabel="Workflow connectors"
              density="compact"
              columns={[
                { key: 'name', header: 'Name', render: (c) => (
                  <div>
                    <div className="font-semibold text-v2-ink text-[12.5px]">{c.name}</div>
                    <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{c.connectorType || c.type}</div>
                  </div>
                )},
                { key: 'endpoint', header: 'Endpoint', render: (c) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{c.host || '—'}:{c.port || '—'}{c.namespace ? ` / ${c.namespace}` : ''}</span>
                )},
                { key: 'auth', header: 'Auth', render: (c) => (
                  <Pill tone="muted" size="sm">{c.authType || 'NONE'}{c.tlsEnabled ? ' · TLS' : ''}</Pill>
                )},
                { key: 'discovered', header: 'Workflows', align: 'right', render: (c) => (
                  <span className="font-mono-v2 text-[12px] text-v2-ink">{(c.workflowsDiscovered || 0).toLocaleString()}</span>
                )},
                { key: 'status', header: 'Status', render: (c) => (
                  <Pill tone={CONN_STATUS_TONE[c.status] || 'muted'} size="sm" dot>{(c.status || 'unknown').toLowerCase()}</Pill>
                )},
                { key: 'lastConn', header: 'Last connected', align: 'right', render: (c) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{fmtRelative(c.lastConnected)}</span>
                )},
                { key: 'actions', header: '', align: 'right', render: (c) => (
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => testConnector(c)} disabled={testing[c.id]} title="Test connectivity">
                      <Activity size={13} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => discoverFor(c)} disabled={discovering[c.id]} title="Discover workflows">
                      {discovering[c.id] ? '…' : <Search size={13} />}
                    </Button>
                    <Button variant="ghost" size="sm" className="!text-v2-danger" onClick={() => deleteConnector(c)} title="Delete">
                      <Trash2 size={13} />
                    </Button>
                  </div>
                )},
              ]}
              rows={connectors}
              rowKey={(c) => c.id}
            />
          )}
        </Card.Body>
      </Card>

      {/* Phase 5B — Add workflow connector: Modal → Slideout per G378.
        * Multi-field create flow (name, type, base URL, scan interval). */}
      {showCreate && (
        <Slideout isOpen onClose={() => setShowCreate(false)} width={620}
                  ariaLabel="Add Workflow Connector"
                  header={<h2 className="text-[15px] font-bold m-0">Add Workflow Connector</h2>}
                  footer={
                    <div className="flex items-center justify-end gap-2 w-full">
                      <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                      <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={submitCreate} disabled={creating}>
                        {creating ? 'Creating…' : 'Add connector'}
                      </Button>
                    </div>
                  }>
          <p className="text-[12.5px] text-v2-ink-secondary mb-3">
            Connects to an external workflow engine (Temporal OSS, Conductor, n8n, custom REST) so the platform
            can discover and protect its workflows.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Name *</label>
              <input type="text" value={form.name} autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Production Temporal OSS"
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Type *</label>
              <select value={form.connectorType}
                onChange={(e) => setForm({ ...form, connectorType: e.target.value })}
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 cursor-pointer">
                <option value="TEMPORAL">Temporal OSS</option>
                <option value="CONDUCTOR">Conductor (Netflix-originated)</option>
                <option value="N8N">n8n</option>
                <option value="CUSTOM_REST">Custom REST</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Host *</label>
              <input type="text" value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="temporal-frontend"
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 font-mono-v2" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Port</label>
              <input type="number" value={form.port}
                onChange={(e) => setForm({ ...form, port: e.target.value })}
                placeholder="7233"
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 font-mono-v2" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Namespace</label>
              <input type="text" value={form.namespace}
                onChange={(e) => setForm({ ...form, namespace: e.target.value })}
                placeholder="default"
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth</label>
              <select value={form.authType}
                onChange={(e) => setForm({ ...form, authType: e.target.value })}
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 cursor-pointer">
                <option value="NONE">None</option>
                <option value="API_KEY">API key</option>
                <option value="BEARER_TOKEN">Bearer token</option>
                <option value="MTLS">mTLS</option>
              </select>
            </div>
          </div>
          <div className="bg-v2-muted rounded-v2-sm border border-v2-border-subtle p-3 mt-3 flex items-center gap-4">
            <label className="text-[12px] flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.tlsEnabled}
                onChange={(e) => setForm({ ...form, tlsEnabled: e.target.checked })} />
              TLS enabled
            </label>
            <label className="text-[12px] flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.autoScanEnabled}
                onChange={(e) => setForm({ ...form, autoScanEnabled: e.target.checked })} />
              Auto-scan
            </label>
            <div className="ml-auto">
              <label className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">Scan every (min)</label>
              <input type="number" value={form.scanIntervalMinutes}
                onChange={(e) => setForm({ ...form, scanIntervalMinutes: e.target.value })}
                className="text-[11.5px] px-2 py-1 border border-v2-border rounded-v2-sm w-20 ml-2 font-mono-v2" />
            </div>
          </div>
          {/* Phase 5B — Footer buttons moved to Slideout footer prop above. */}
        </Slideout>
      )}

      {/* G466/G471 — Connector delete now uses an inline confirmation
          panel rendered above the connector list, instead of a backdrop
          Modal. Keeps the user grounded in the Sources tab and avoids
          the "modal pop-up" pattern that the user explicitly called
          out as non-V2. Panel only appears when a target is set;
          dismissing it leaves the page untouched. */}
      {deleteConnectorTarget && (
        <div className="mb-4 border border-v2-danger bg-v2-danger-soft rounded-v2-md p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-v2-danger flex items-center justify-center flex-shrink-0">
            <Trash2 size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-v2-ink mb-1">
              Delete connector "{deleteConnectorTarget.name}"?
            </div>
            <p className="text-[12px] text-v2-ink-secondary leading-relaxed mb-2">
              New discovery scans will stop running on this source. Existing workflows
              previously discovered from <strong>{deleteConnectorTarget.name}</strong> stay
              in the inventory and remain protected — only the live connection is removed.
            </p>
            {deleteConnectorTarget.workflowCount > 0 && (
              <div className="bg-v2-warning-soft border border-v2-warning rounded-v2-sm px-3 py-1.5 mb-3 text-[11.5px] text-v2-warning-strong">
                <strong>{deleteConnectorTarget.workflowCount}</strong> workflow(s) discovered from this connector. They will be retained.
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />} onClick={confirmDeleteConnector}>
                Delete connector
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDeleteConnectorTarget(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation drift modal */}
      {reconcileDrift && (
        <Modal isOpen onClose={() => setReconcileDrift(null)} title="Reconcile drift detected" width={560}>
          <p className="text-[12.5px] text-v2-ink-secondary mb-3">
            The latest discovery scan on <strong>{reconcileDrift.connectorName}</strong> found{' '}
            <strong>{reconcileDrift.orphans.length}</strong> workflow(s) that were previously protected
            but are no longer present upstream. Tombstoning marks them as orphaned (preserves audit history,
            stops further enforcement attempts).
          </p>
          <ul className="space-y-1 max-h-[280px] overflow-auto mb-3">
            {reconcileDrift.orphans.map((o, i) => (
              <li key={o.id || i} className="text-[12px] border-l-2 border-v2-warning pl-2">
                <span className="font-mono-v2">{o.workflowType || o.name || o.id}</span>
                <span className="text-v2-ink-muted ml-2">{o.lastSeen ? `last seen ${fmtRelative(o.lastSeen)}` : ''}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 pt-3 border-t border-v2-border-subtle">
            <Button variant="ghost" size="sm" onClick={() => setReconcileDrift(null)}>Skip reconcile</Button>
            <Button variant="primary" size="sm" onClick={() => confirmReconcile(reconcileDrift.orphans.map(o => o.id))}>
              Tombstone all
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Tab 3: Workflows (canonical inventory pattern)
 * ────────────────────────────────────────────────────────────────── */

/**
 * ConversionTab (G466)
 *
 * Surfaces the latest conversion report for a discovered workflow:
 *   - When the workflow was converted, what version was produced
 *   - Number of source nodes vs. resulting Canvas nodes (was there
 *     a 1:1 mapping or did the converter expand/synthesize anything?)
 *   - Fallback usage (tasks the converter couldn't map natively)
 *   - Validation warnings (e.g. parallel_join not paired with fork,
 *     terminate with outbound, multi-default fan-out — the same
 *     malformed shapes the canvas pipeline now rejects per G455/G458/G459)
 *
 * Operators use this tab to decide whether to Re-convert from source.
 * If the conversion report shows legacy validation warnings, a fresh
 * re-conversion (with the source fixes in place) will produce a
 * cleaner Native CEG.
 */
function ConversionTab({ selected, addToast, reconvertBusyId, onReconvert, onNavigateCanvas }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selected?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.discoveryGetConversionReport?.(selected.id)
      .then((res) => { if (!cancelled) setReport(res?.data || res || null); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load conversion report'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.id]);

  return (
    <Card padding="sm">
      <Card.Header title="Conversion report"
        actions={
          <div className="flex items-center gap-1">
            {(selected.nativeWorkflowId || selected.status === 'PROTECTED' || selected.status === 'MAPPED'
              || ['ANALYZED','MAPPED','COMMITTED'].includes(selected.mapperProgress)) && (
              <Button variant="primary" size="sm" iconLeft={<RotateCw size={13} />}
                onClick={onReconvert} disabled={reconvertBusyId === selected.id}>
                {reconvertBusyId === selected.id ? 'Re-converting…' : 'Re-convert'}
              </Button>
            )}
            {selected.nativeWorkflowId && (
              <Button variant="ghost" size="sm" iconLeft={<FileText size={13} />} onClick={onNavigateCanvas}>
                Open Native CEG
              </Button>
            )}
          </div>
        } />
      <Card.Body>
        {!selected.nativeWorkflowId ? (
          <div className="text-[12.5px] text-v2-ink-muted">
            This workflow hasn't been converted to Canvas yet. Run <strong>Protect</strong> from the Summary tab to enter the GEL pipeline and produce a Native CEG.
          </div>
        ) : loading ? (
          <div className="text-[12.5px] text-v2-ink-muted">Loading conversion report…</div>
        ) : error ? (
          <div className="text-[12.5px] text-v2-danger">{error}</div>
        ) : report ? (
          <div className="space-y-3 text-[12.5px]">
            <dl className="grid grid-cols-2 gap-3">
              <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Native CEG version</dt>
                <dd className="text-v2-ink mt-0.5 font-mono-v2">v{report.version || report.nativeVersion || '?'}</dd></div>
              <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Converted at</dt>
                <dd className="text-v2-ink mt-0.5">{report.convertedAt ? new Date(report.convertedAt).toLocaleString() : '—'}</dd></div>
              <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Source → Canvas</dt>
                <dd className="text-v2-ink mt-0.5 font-mono-v2">
                  {report.sourceNodeCount || '?'} → {report.canvasNodeCount || '?'} nodes
                </dd></div>
              <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Connections</dt>
                <dd className="text-v2-ink mt-0.5 font-mono-v2">{report.connectionCount || '?'}</dd></div>
            </dl>
            {Array.isArray(report.fallbacks) && report.fallbacks.length > 0 && (
              <div>
                <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Fallback mappings</dt>
                <dd>
                  <ul className="list-disc list-inside space-y-0.5 ml-1">
                    {report.fallbacks.map((f, i) => (
                      <li key={i} className="text-v2-ink">
                        <span className="font-mono-v2 text-[11px] text-v2-warning">{f.sourceType || '?'}</span> → <span className="font-mono-v2 text-[11px]">{f.canvasType || 'generic'}</span>
                        {f.reason && <span className="text-v2-ink-muted"> ({f.reason})</span>}
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
            {Array.isArray(report.warnings) && report.warnings.length > 0 && (
              <div>
                <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Validation warnings</dt>
                <dd>
                  <ul className="list-disc list-inside space-y-0.5 ml-1">
                    {report.warnings.map((w, i) => (
                      <li key={i} className="text-v2-warning">{typeof w === 'string' ? w : w.message || JSON.stringify(w)}</li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
            {report.description && (
              <div className="bg-v2-action-soft text-v2-action-strong rounded-v2-sm px-3 py-2 text-[11.5px]">{report.description}</div>
            )}
          </div>
        ) : (
          <div className="text-[12.5px] text-v2-ink-muted">No conversion report on file yet. Run <strong>Re-convert from source</strong> above to produce a fresh report.</div>
        )}
      </Card.Body>
    </Card>
  );
}

function WorkflowsTab({ workflows, connectors, bindings, addToast, reload, navigate, loading }) {
  const [facets, setFacets] = useState(() => ({
    status: new Set(), risk: new Set(), connector: new Set(),
  }));
  const toggleFacet = (g, o, c) => setFacets(p => {
    const n = new Set(p[g] || []);
    c ? n.add(o) : n.delete(o);
    return { ...p, [g]: n };
  });
  const clearFacets = () => setFacets({ status: new Set(), risk: new Set(), connector: new Set() });

  const [search, setSearch] = useState('');
  const [density, setDensity] = useState(() => { try { return localStorage.getItem('wfd-density') || 'comfortable'; } catch { return 'comfortable'; } });
  const cycleDensity = () => { const n = density === 'comfortable' ? 'compact' : 'comfortable'; setDensity(n); try { localStorage.setItem('wfd-density', n); } catch {} };

  /* G466 — Connector lookup: resolve a workflow's source type by joining
   * on connectionId against the loaded connectors list. The discovery API's
   * list response does NOT include the connector type inline (the list
   * endpoint avoids the join for performance), but every workflow record
   * carries connectionId pointing to its source connector. This map gives
   * us O(1) lookup from connectionId → connector record (name + type).
   *
   * Pre-G466, the Source column used a buggy fallback
   *   (w.temporalNamespace ? 'temporal' : null)
   * which falsely labelled EVERY workflow as 'temporal' because the
   * discovery service stores 'temporalNamespace' as a generic namespace
   * identifier on all records (even non-Temporal OSS connectors). The result
   * was that a Conductor OSS connector showed its
   * workflows as "temporal" — wildly misleading. Now we look up the
   * actual connector record by id and use its type field.
   */
  const connectorById = useMemo(() => {
    const map = new Map();
    for (const c of (connectors || [])) {
      if (c?.id) map.set(c.id, c);
    }
    return map;
  }, [connectors]);

  const resolveSourceType = useCallback((w) => {
    if (!w) return null;
    // 1. Inline field (some endpoints DO include it; honor it first).
    if (w.connectorType) return w.connectorType;
    if (w.connectorName) return w.connectorName;
    // 2. Join via connectionId → connector record → type.
    const conn = w.connectionId ? connectorById.get(w.connectionId) : null;
    if (conn) return conn.type || conn.connectorType || conn.kind || conn.name;
    // 3. Platform field (legacy alias).
    if (w.platform) return w.platform;
    // 4. Last resort: temporalNamespace is NOT a source signal — only
    //    return null so the column renders "—" rather than misleading
    //    'temporal' for non-Temporal OSS sources.
    return null;
  }, [connectorById]);

  const connectorOptions = useMemo(() => {
    const s = new Set();
    for (const w of workflows) {
      const src = resolveSourceType(w);
      if (src) s.add(src);
    }
    return Array.from(s).sort();
  }, [workflows, resolveSourceType]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'status', label: 'Status', options: WF_STATUS_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'risk',   label: 'Risk',   options: RISK_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'connector', label: 'Connector', options: connectorOptions.map(c => ({ key: c, label: c })) },
  ]), [connectorOptions]);

  const filtered = useMemo(() => workflows.filter(w => {
    if (facets.status.size > 0 && !facets.status.has(w.status)) return false;
    const r = (w.riskTier || w.risk || 'low').toLowerCase();
    if (facets.risk.size > 0 && !facets.risk.has(r)) return false;
    /* G466 — Use resolveSourceType so the facet matches the rendered
       Source column (was matching a wrong fallback that produced empty
       filter sets on non-Temporal OSS sources). */
    if (facets.connector.size > 0) {
      const src = resolveSourceType(w);
      if (!src || !facets.connector.has(src)) return false;
    }
    if (search && !`${w.workflowType || ''} ${w.name || ''} ${w.namespace || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [workflows, facets, search, resolveSourceType]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = workflows.filter(w => {
          if (g.key === 'status') return w.status === opt.key;
          if (g.key === 'risk')   return (w.riskTier || w.risk || 'low').toLowerCase() === opt.key;
          /* G466 — Use resolved source type so counts line up with the
             Source column render (was using a stale field that excluded
             every CONDUCTOR/n8n/custom workflow from the count). */
          return resolveSourceType(w) === opt.key;
        }).length;
      }
    }
    return counts;
  }, [workflows, FACET_GROUPS, resolveSourceType]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({ ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const k of facets[g.key] || []) {
      const opt = g.options.find(o => o.key === k);
      chips.push({ groupKey: g.key, optionKey: k, label: `${g.label}: ${opt?.label || k}` });
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('summary');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('summary'); };
  const closeSlideout = () => setSelected(null);

  const handleProtect = async (w) => {
    try {
      await api.discoveryProtectWorkflow?.(w.id);
      addToast({ tone: 'success', title: 'Protection initiated',
        description: `${w.workflowType || w.name} entered the GEL pipeline.` });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Protect failed', description: err?.message });
    }
  };

  /* G466 — Top-level Discover action restored.
   * V1 had a single "Discover Workflows" button at the page header that
   * iterated through every CONNECTED connector and called the discovery
   * scan endpoint, then refreshed the workflow list. V2 originally moved
   * this to be per-connector in the Sources tab only — operators kept
   * losing the button entirely. This restores the top-level scan: it
   * iterates every CONNECTED connector and reports per-connector results
   * in the toast, then reloads.
   *
   * Connectors with status === 'ERROR' or 'DISABLED' are skipped (calling
   * .discover on a disabled connector returns 4xx). If no connectors are
   * CONNECTED, the toast surfaces that fact so the operator knows to
   * go to Sources first.
   */
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const handleDiscoverAll = async () => {
    const eligible = (connectors || []).filter((c) => c && c.id && (c.status === 'CONNECTED' || !c.status));
    if (eligible.length === 0) {
      addToast({ tone: 'warning', title: 'No connected sources',
        description: 'Add or activate a connector on the Sources tab before running discovery.' });
      return;
    }
    setDiscoverBusy(true);
    let succeeded = 0;
    let failed = 0;
    const failures = [];
    for (const c of eligible) {
      try {
        await api.discoveryTriggerDiscover?.(c.id);
        succeeded += 1;
      } catch (err) {
        failed += 1;
        failures.push(`${c.name || c.id}: ${err?.message || 'unknown error'}`);
      }
    }
    setDiscoverBusy(false);
    if (failed === 0) {
      addToast({ tone: 'success', title: `Discovery complete (${succeeded} source${succeeded === 1 ? '' : 's'})`,
        description: 'Refreshing workflow inventory…' });
    } else if (succeeded > 0) {
      addToast({ tone: 'warning', title: `Discovery partial (${succeeded} ok, ${failed} failed)`,
        description: failures.slice(0, 2).join('; ') });
    } else {
      addToast({ tone: 'danger', title: 'Discovery failed on all sources',
        description: failures.slice(0, 2).join('; ') });
    }
    reload();
  };

  /* G466 — Per-row "Re-convert from source" action.
   *
   * This is the endpoint operators have been missing: POST /reconvert-native
   * fetches the LATEST source workflow definition (from Temporal OSS / Conductor)
   * and runs the full conversion pipeline again, producing a fresh native
   * version (snapshot-bumped: v37 → v38 → …). Used when:
   *   (a) The upstream source has been updated and the platform-managed
   *       Native CEG is now stale.
   *   (b) The persisted Native CEG is broken (e.g. legacy DPG-past-sentinel
   *       garbage from a buggy enforcement injection — see G459/G462) and
   *       you want to regenerate from a clean source.
   *
   * The pre-G466 V2 page exposed "Reset & re-convert" on the Visual Mapper
   * tab but that endpoint (/mapper/reset) only resets the mapper's local
   * stage tracking — it does NOT pull fresh source nor re-run conversion.
   * The truly fresh-conversion endpoint had no UI button at all.
   */
  const [reconvertBusyId, setReconvertBusyId] = useState(null);
  const handleReconvert = async (w) => {
    if (reconvertBusyId) return;
    setReconvertBusyId(w.id);
    try {
      const res = await api.discoveryReconvertNative?.(w.id);
      const data = res?.data || res;
      addToast({ tone: 'success', title: 'Re-converted from source',
        description: `${w.workflowType || w.name} is now v${data?.version || '?'}${data?.snapshotVersion ? ` (snapshot ${data.snapshotVersion})` : ''}.` });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Re-conversion failed',
        description: err?.message || 'Check that the source workflow is still reachable.' });
    } finally {
      setReconvertBusyId(null);
    }
  };

  /* G466 — Per-row "Analyze" action.
   *
   * Triggers POST /workflows/{id}/analyze which runs the deep-analysis
   * pipeline: activity-type extraction, governance score, data-flow
   * detection, recommended-controls suggestion. The findings populate
   * the workflow's detectedAgents / detectedTools / detectedDataFlows /
   * recommendedControls fields shown in the slideout. Distinct from
   * Protect (which initiates the full GEL pipeline) — Analyze is a
   * non-mutating discovery refresh.
   */
  const [analyzeBusyId, setAnalyzeBusyId] = useState(null);
  const handleAnalyze = async (w) => {
    if (analyzeBusyId) return;
    setAnalyzeBusyId(w.id);
    try {
      await api.discoveryAnalyzeWorkflow?.(w.id);
      addToast({ tone: 'success', title: 'Analysis updated',
        description: `${w.workflowType || w.name} re-scanned for activities, data flows, protection score.` });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Analyze failed', description: err?.message });
    } finally {
      setAnalyzeBusyId(null);
    }
  };

  const [execModal, setExecModal] = useState(null);

  const openExec = (w) => setExecModal({ workflow: w, input: '{}', mode: 'GEL', submitting: false });
  const submitExec = async () => {
    if (!execModal) return;
    let payload;
    try { payload = JSON.parse(execModal.input || '{}'); }
    catch { addToast({ tone: 'danger', title: 'Input must be valid JSON' }); return; }
    setExecModal({ ...execModal, submitting: true });
    try {
      const res = await api.discoveryStartExternalWorkflow?.(execModal.workflow.id, payload, execModal.mode);
      const data = res?.data || res;
      if (data?.decision === 'BLOCKED' || data?.blocked) {
        addToast({ tone: 'danger', title: 'Execution blocked',
          description: data?.reason || data?.message || 'Policy or DPG denied this run.' });
      } else {
        addToast({ tone: 'success', title: 'Execution started',
          description: `executionId=${data?.executionId || '—'}` });
      }
      setExecModal(null);
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Execution failed', description: err?.message });
      setExecModal((m) => m ? { ...m, submitting: false } : null);
    }
  };

  const bindingFor = (w) => bindings.find(b => b.discoveredWorkflowId === w.id || b.workflowId === w.id);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total" value={workflows.length} tone="cyan" />
        <MetricCard label="Protected"   value={workflows.filter(w => w.status === 'PROTECTED').length} tone="success" />
        <MetricCard label="Registering" value={workflows.filter(w => w.status === 'REGISTERING').length} tone="warning" />
        <MetricCard label="Orphaned"    value={workflows.filter(w => w.status === 'ORPHANED').length}
          tone={workflows.filter(w => w.status === 'ORPHANED').length > 0 ? 'danger' : 'muted'} />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {/* G466 — Top-level "Discover from sources" action. Iterates every
            CONNECTED connector and triggers its discovery scan. The button
            sits on the LEFT (primary action) — the secondary Density toggle
            and search input stay on the right where operators expect
            view-controls. Disabled state during the in-flight scan keeps
            users from re-triggering and shows a spinner-style icon. */}
        <Button
          variant="primary"
          size="sm"
          iconLeft={<ScanSearch size={14} />}
          onClick={handleDiscoverAll}
          disabled={discoverBusy}
        >
          {discoverBusy ? 'Discovering…' : 'Discover from sources'}
        </Button>
        <span className="text-[11px] text-v2-ink-muted ml-1">
          {(connectors || []).filter((c) => c?.status === 'CONNECTED' || !c?.status).length} connector(s) ready
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search workflows…"
              className="text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-64" />
          </div>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
        </div>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Workflow filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState icon={<Workflow size={20} />}
            title={workflows.length === 0 ? 'No workflows discovered yet' : 'No workflows match filters'}
            description={workflows.length === 0
              ? 'Configure a connector on the Sources tab, then run discovery to populate this list.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={workflows.length > 0
              ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
              : undefined} />
        ) : (
          <Table
            ariaLabel="Discovered workflows"
            density={density}
            columns={[
              { key: 'name', header: 'Workflow', render: (w) => (
                <div>
                  <div className="font-semibold text-v2-ink text-[12.5px]">{w.workflowType || w.name}</div>
                  {/* G219 — Backend uses `temporalNamespace`; fall back to legacy `namespace`. */}
                  <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate">{w.temporalNamespace || w.namespace || '—'} · {w.taskQueue || w.queue || '—'}</div>
                </div>
              )},
              { key: 'connector', header: 'Source', render: (w) => {
                /* G466 — Resolve source type by joining on connectionId
                 * against the loaded connectors list. The discovery
                 * service stores `temporalNamespace` on every workflow
                 * (it's a generic namespace identifier, not a source-type
                 * signal), so the old G219 fallback falsely labelled
                 * EVERY workflow as 'temporal' regardless of the actual
                 * connector type. Now we look up by connectionId and
                 * surface the real connector type (CONDUCTOR for the
                 * Conductor OSS connector seen in the wild, TEMPORAL
                 * for Temporal OSS-backed connectors, n8n, custom, etc.).
                 * The connector name is also shown as the row subtitle
                 * (when available) for full traceability. */
                const src = resolveSourceType(w);
                const conn = w.connectionId ? connectorById.get(w.connectionId) : null;
                if (!src) return <span className="text-[11px] text-v2-ink-muted">—</span>;
                const tone = src.toUpperCase() === 'TEMPORAL' ? 'cyan'
                  : src.toUpperCase() === 'CONDUCTOR' ? 'trust'
                  : src.toLowerCase() === 'n8n' ? 'warning'
                  : 'muted';
                return (
                  <div className="flex flex-col items-start gap-0.5">
                    <Pill tone={tone} size="sm">{src.toLowerCase()}</Pill>
                    {conn?.name && <span className="text-[10px] text-v2-ink-muted font-mono-v2 truncate">{conn.name}</span>}
                  </div>
                );
              }},
              { key: 'risk', header: 'Risk', render: (w) => {
                const r = (w.riskTier || w.risk || 'low').toLowerCase();
                return <Pill tone={RISK_TONE[r] || 'muted'} size="sm">{r}</Pill>;
              }},
              { key: 'status', header: 'Status', render: (w) => (
                <Pill tone={WF_STATUS_TONE[w.status] || 'muted'} size="sm" dot>{(w.status || 'unknown').toLowerCase()}</Pill>
              )},
              { key: 'exec', header: 'Execs / 30d', align: 'right', render: (w) => (
                <span className="font-mono-v2 text-[12px]">{(w.executionCount || 0).toLocaleString()}</span>
              )},
              { key: 'fail', header: 'Fail rate', align: 'right', render: (w) => {
                const f = parseFloat(w.failureRate || 0);
                if (!Number.isFinite(f) || f === 0) return <span className="text-v2-ink-muted">—</span>;
                const pct = f <= 1 ? Math.round(f * 100) : Math.round(f);
                return <span className={`font-mono-v2 text-[12px] ${pct > 10 ? 'text-v2-danger' : pct > 2 ? 'text-v2-warning' : 'text-v2-success'}`}>{pct}%</span>;
              }},
              { key: 'last', header: 'Discovered', align: 'right', render: (w) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{fmtRelative(w.firstDiscoveredAt || w.createdAt)}</span>
              )},
              { key: 'actions', header: '', align: 'right', render: (w) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {/* G466 — Analyze (re-scan source metadata). Available
                      for any non-PROTECTED workflow. Distinct from Protect:
                      Analyze refreshes detected activities / data flows /
                      governance score without entering the GEL pipeline. */}
                  {w.status !== 'PROTECTED' && (
                    <Button variant="ghost" size="sm" className="!text-v2-cyan"
                      onClick={() => handleAnalyze(w)}
                      disabled={analyzeBusyId === w.id}
                      title="Analyze (refresh activities, data flows, protection score)">
                      <ClipboardList size={13} />
                    </Button>
                  )}
                  {w.status !== 'PROTECTED' && w.status !== 'REGISTERING' && (
                    <Button variant="ghost" size="sm" className="!text-v2-success" onClick={() => handleProtect(w)} title="Protect (start GEL)">
                      <ShieldCheck size={13} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openExec(w)} title="Execute">
                    <Play size={13} />
                  </Button>
                  {/* G466 — Re-convert from source (POST /reconvert-native).
                      Available for any workflow that has been through the
                      conversion pipeline before — recognized by EITHER:
                        • status === 'PROTECTED' (full GEL pipeline ran)
                        • mapperProgress in {ANALYZED, MAPPED, COMMITTED}
                        • a non-null nativeWorkflowId (native CEG exists)
                      The widest gate covers edge cases like the post-reset
                      state where mapperProgress is back to NOT_STARTED and
                      nativeWorkflowId was detached, but status === PROTECTED
                      still indicates the workflow was once converted. The
                      backend reconvert endpoint resolves the source from
                      the discovery record alone, so it works in all these
                      cases.
                      Used to regenerate broken Native CEGs from a clean
                      source — this is the endpoint that was missing in
                      V2 and that G459's broken Native Insurance Claim v37
                      needs to regenerate from. */}
                  {(w.nativeWorkflowId || w.status === 'PROTECTED' || w.status === 'MAPPED'
                    || ['ANALYZED','MAPPED','COMMITTED'].includes(w.mapperProgress)) && (
                    <Button variant="ghost" size="sm" className="!text-v2-warning"
                      onClick={() => handleReconvert(w)}
                      disabled={reconvertBusyId === w.id}
                      title="Re-convert from source (pull latest from Temporal OSS/Conductor and regenerate)">
                      <RotateCw size={13} />
                    </Button>
                  )}
                  {w.nativeWorkflowId && (
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/canvas?workflowId=${w.nativeWorkflowId}`)} title="Open in Canvas">
                      <FileText size={13} />
                    </Button>
                  )}
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(w) => w.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="workflows" />
      </div>

      <Slideout
        isOpen={!!selected} onClose={closeSlideout}
        ariaLabel={selected ? `${selected.workflowType || selected.name} detail` : 'Workflow detail'}
        width={560}
        tabs={[
          { id: 'summary', label: 'Summary' },
          { id: 'analysis', label: 'Analysis' },
          { id: 'conversion', label: 'Conversion' },
          { id: 'govern', label: 'Protect' },
          { id: 'execute', label: 'Execute' },
          { id: 'audit',  label: 'Audit' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={WF_STATUS_TONE[selected.status]} size="sm" dot>{(selected.status || 'unknown').toLowerCase()}</Pill>
              <Pill tone={RISK_TONE[(selected.riskTier || selected.risk || 'low').toLowerCase()]} size="sm">
                {(selected.riskTier || selected.risk || 'low').toLowerCase()}
              </Pill>
              <Pill tone="muted" size="sm">{selected.connectorType || 'unknown'}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.workflowType || selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary font-mono-v2">{selected.namespace || '—'} · {selected.taskQueue || selected.queue || '—'}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {selected.status !== 'PROTECTED' && selected.status !== 'REGISTERING' && (
                <Button variant="primary" size="sm" iconLeft={<ShieldCheck size={13} />} onClick={() => handleProtect(selected)}>Protect</Button>
              )}
              <Button variant="secondary" size="sm" iconLeft={<Play size={13} />} onClick={() => openExec(selected)}>Execute</Button>
              {(selected.nativeWorkflowId || selected.status === 'PROTECTED' || selected.status === 'MAPPED'
                || ['ANALYZED','MAPPED','COMMITTED'].includes(selected.mapperProgress)) && (
                <Button variant="secondary" size="sm" iconLeft={<RotateCw size={13} />}
                  onClick={() => handleReconvert(selected)}
                  disabled={reconvertBusyId === selected.id}>
                  {reconvertBusyId === selected.id ? 'Re-converting…' : 'Re-convert from source'}
                </Button>
              )}
              {selected.status !== 'PROTECTED' && (
                <Button variant="ghost" size="sm" iconLeft={<ClipboardList size={13} />}
                  onClick={() => handleAnalyze(selected)}
                  disabled={analyzeBusyId === selected.id}>
                  {analyzeBusyId === selected.id ? 'Analyzing…' : 'Analyze'}
                </Button>
              )}
              {selected.nativeWorkflowId && (
                <Button variant="ghost" size="sm" iconLeft={<FileText size={13} />} onClick={() => navigate(`/canvas?workflowId=${selected.nativeWorkflowId}`)}>Open in Canvas</Button>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'summary' && (
          <Card padding="sm">
            <Card.Header title="Workflow detail" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Discovered ID</dt>
                  <dd className="text-v2-ink mt-0.5 font-mono-v2 text-[11px]">{(selected.id || '').slice(0, 16)}…</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Native CEG</dt>
                  <dd className="text-v2-ink mt-0.5 font-mono-v2 text-[11px]">{selected.nativeWorkflowId ? selected.nativeWorkflowId.slice(0, 16) + '…' : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Data classification</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.dataClassification || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Avg duration</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.avgDuration ? `${selected.avgDuration} ms` : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Executions (30d)</dt>
                  <dd className="text-v2-ink mt-0.5 font-mono-v2">{(selected.executionCount || 0).toLocaleString()}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">First seen</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.firstDiscoveredAt ? new Date(selected.firstDiscoveredAt).toLocaleString() : '—'}</dd></div>
              </dl>
              {selected.tombstonedAt && (
                <div className="mt-3 border-l-4 border-v2-danger bg-v2-danger-soft px-3 py-2 rounded-v2-sm text-[12px]">
                  <strong className="text-v2-danger">Tombstoned</strong> {fmtRelative(selected.tombstonedAt)} — {selected.tombstoneReason || 'no reason given'}
                </div>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'analysis' && (
          /* G466 — Analysis tab: surface deep-analysis findings from
             POST /workflows/{id}/analyze. The discovery API enriches
             detectedAgents / detectedTools / detectedDataFlows /
             recommendedControls when Analyze runs. V1 surfaced these
             inline in the expanded row; V2 puts them in a dedicated
             slideout tab for full visibility. */
          <Card padding="sm">
            <Card.Header title="Activity & data-flow analysis"
              actions={
                <Button variant="ghost" size="sm" iconLeft={<ClipboardList size={13} />}
                  onClick={() => handleAnalyze(selected)}
                  disabled={analyzeBusyId === selected.id}>
                  {analyzeBusyId === selected.id ? 'Re-analyzing…' : 'Re-analyze'}
                </Button>
              } />
            <Card.Body>
              <div className="space-y-3 text-[12.5px]">
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Activity types</dt>
                  <dd>
                    {Array.isArray(selected.activityTypes) && selected.activityTypes.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {selected.activityTypes.map((a, i) => <Pill key={i} tone="cyan" size="sm">{a}</Pill>)}
                        </div>
                      : <span className="text-v2-ink-muted">No activities detected yet — run Analyze.</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Detected agents</dt>
                  <dd>
                    {Array.isArray(selected.detectedAgents) && selected.detectedAgents.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {selected.detectedAgents.map((a, i) => <Pill key={i} tone="trust" size="sm">{typeof a === 'string' ? a : a.name || a.id}</Pill>)}
                        </div>
                      : <span className="text-v2-ink-muted">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Detected tools</dt>
                  <dd>
                    {Array.isArray(selected.detectedTools) && selected.detectedTools.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {selected.detectedTools.map((t, i) => <Pill key={i} tone="muted" size="sm">{typeof t === 'string' ? t : t.name || t.id}</Pill>)}
                        </div>
                      : <span className="text-v2-ink-muted">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Detected data flows</dt>
                  <dd>
                    {Array.isArray(selected.detectedDataFlows) && selected.detectedDataFlows.length > 0
                      ? <ul className="list-disc list-inside space-y-0.5 ml-1">
                          {selected.detectedDataFlows.map((f, i) => <li key={i} className="text-v2-ink">{typeof f === 'string' ? f : `${f.source || '?'} → ${f.target || '?'}`}</li>)}
                        </ul>
                      : <span className="text-v2-ink-muted">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Recommended controls</dt>
                  <dd>
                    {Array.isArray(selected.recommendedControls) && selected.recommendedControls.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {selected.recommendedControls.map((c, i) => <Pill key={i} tone="warning" size="sm">{typeof c === 'string' ? c : c.label || c.id}</Pill>)}
                        </div>
                      : <span className="text-v2-ink-muted">No control recommendations yet.</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold mb-1">Protection score</dt>
                  <dd>
                    {Number.isFinite(Number(selected.governanceScore)) ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-v2-border-subtle rounded-v2-pill overflow-hidden">
                          <div className={`h-full ${Number(selected.governanceScore) > 75 ? 'bg-v2-success' : Number(selected.governanceScore) > 50 ? 'bg-v2-warning' : 'bg-v2-danger'}`}
                            style={{ width: `${Math.min(100, Math.max(0, Number(selected.governanceScore)))}%` }} />
                        </div>
                        <span className="font-mono-v2 text-[12px]">{Math.round(Number(selected.governanceScore))}/100</span>
                      </div>
                    ) : <span className="text-v2-ink-muted">—</span>}
                  </dd>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'conversion' && (
          /* G466 — Conversion tab: surface the conversion report from
             GET /workflows/{id}/conversion-report and expose primary
             re-conversion actions. This is where operators see WHY
             the converter behaved the way it did and decide whether
             to re-run from a fresh source. */
          <ConversionTab selected={selected} addToast={addToast}
            reconvertBusyId={reconvertBusyId} onReconvert={() => handleReconvert(selected)}
            onNavigateCanvas={() => navigate(`/canvas?workflowId=${selected.nativeWorkflowId}`)} />
        )}
        {selected && slideoutTab === 'govern' && (
          <Card padding="sm">
            <Card.Header title="GEL binding + enforcement" />
            <Card.Body>
              {(() => {
                const b = bindingFor(selected);
                if (!b) {
                  return (
                    <div>
                      <p className="text-[12.5px] text-v2-ink-muted">No GEL binding yet. Use Protect to enter the pipeline.</p>
                      {selected.status !== 'PROTECTED' && (
                        <Button variant="primary" size="sm" className="mt-2" iconLeft={<ShieldCheck size={13} />} onClick={() => handleProtect(selected)}>Start protection</Button>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="space-y-2 text-[12.5px]">
                    <div className="flex items-center gap-2">
                      <Pill tone={ENF_MODE_TONE[b.enforcementMode] || 'muted'} size="sm">{b.enforcementMode || 'MONITOR'}</Pill>
                      <span className="text-v2-ink-muted text-[11px]">since {fmtRelative(b.activatedAt || b.createdAt)}</span>
                    </div>
                    <p className="text-[11.5px] text-v2-ink-muted">Change enforcement mode:</p>
                    <div className="flex items-center gap-1.5">
                      {ENFORCEMENT_MODES.map((m) => (
                        <Button key={m} size="sm"
                          variant={b.enforcementMode === m ? 'primary' : 'ghost'}
                          onClick={async () => {
                            try {
                              await api.discoveryUpdateBindingMode?.(b.id, m);
                              addToast({ tone: 'success', title: `Enforcement → ${m}` });
                              reload();
                            } catch (err) {
                              addToast({ tone: 'danger', title: 'Update failed', description: err?.message });
                            }
                          }}>{m}</Button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'execute' && (
          <Card padding="sm">
            <Card.Header title="Execute workflow" actions={
              <Button variant="primary" size="sm" iconLeft={<Play size={13} />} onClick={() => openExec(selected)}>Open input</Button>
            } />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink-muted">Trigger an execution through the GEL gate (policy + DPG + audit) or direct to the source engine.</p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'audit' && (
          <Card padding="sm">
            <Card.Header title="Audit trail" actions={
              /* G436 — Full audit deep-link. Inside the actions prop
                 expression-container the value must be a single JSX
                 expression — a sibling comment node makes it two
                 expressions and crashes the bundle. */
              <Button variant="ghost" size="sm" onClick={() => navigate(`/workflow-audit?workflowId=${selected.id}`)}>Full audit →</Button>
            } />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink-muted">Per-execution audit chain entries are available on the Audit page filtered to this workflow.</p>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* Phase 5B — Execute workflow: Modal → Slideout per G378. Mode
        * picker + JSON input + start action; multi-section. */}
      {execModal && (
        <Slideout isOpen onClose={() => setExecModal(null)} width={620}
                  ariaLabel={`Execute · ${execModal.workflow.workflowType || execModal.workflow.name}`}
                  header={<h2 className="text-[15px] font-bold m-0 truncate">{`Execute · ${execModal.workflow.workflowType || execModal.workflow.name}`}</h2>}
                  footer={
                    <div className="flex items-center justify-end gap-2 w-full">
                      <Button variant="ghost" size="sm" onClick={() => setExecModal(null)}>Cancel</Button>
                      <Button variant="primary" size="sm" iconLeft={<Play size={13} />} onClick={submitExec} disabled={execModal.submitting}>
                        {execModal.submitting ? 'Starting…' : 'Start execution'}
                      </Button>
                    </div>
                  }>
          <p className="text-[12.5px] text-v2-ink-secondary mb-3">
            Run this external workflow with the JSON input below. Choose how the platform routes the call.
          </p>
          <div className="mb-3">
            <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Mode</label>
            <div className="flex gap-2 mt-1.5">
              {[
                { key: 'GEL',    label: 'Protected (GEL)', desc: 'Routes through policy + DPG + audit' },
                { key: 'DIRECT', label: 'Direct',         desc: 'Bypasses governance — admin only' },
              ].map((m) => (
                <button key={m.key} type="button"
                  onClick={() => setExecModal({ ...execModal, mode: m.key })}
                  className={'text-left px-3 py-2 rounded-v2-sm border ' +
                    (execModal.mode === m.key ? 'border-v2-action bg-v2-action-soft text-v2-action-strong' : 'border-v2-border-subtle hover:border-v2-action')}>
                  <div className="text-[12px] font-semibold">{m.label}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Input (JSON)</label>
            <textarea value={execModal.input}
              onChange={(e) => setExecModal({ ...execModal, input: e.target.value })}
              rows={6}
              className="text-[11.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
              placeholder='{"customerId": "..."}' />
          </div>
          {/* Phase 5B — Footer buttons moved to Slideout footer prop above. */}
        </Slideout>
      )}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Tab 4: Visual Mapper — full 5-stage wizard (G159)
 *
 * Pipeline: SELECT (0) → ANALYZE (1) → MAP+REVIEW (2) → COMMIT (3) → DONE (4)
 *
 * Smart resume on workflow click — if the backend already has progress
 * (ANALYZED / REVIEW_READY / COMMITTED) we jump to the matching step
 * instead of re-running analysis. Mirrors v1 handleSelectWorkflow().
 * ────────────────────────────────────────────────────────────────── */

const MAPPER_STEPS = [
  { id: 0, label: 'Select',  desc: 'Pick a workflow to convert' },
  { id: 1, label: 'Analyze', desc: 'Inspect the intermediate representation' },
  { id: 2, label: 'Map',     desc: 'Review + edit the generated Canvas JSON' },
  { id: 3, label: 'Commit',  desc: 'Materialize as a native CEG' },
  { id: 4, label: 'Done',    desc: 'Open in Canvas or reset' },
];

function MapperTab({ workflows, connectors, addToast, navigate, reload }) {
  const [step, setStep] = useState(0);
  const [selectedWf, setSelectedWf] = useState(null);
  const [analysis, setAnalysis] = useState(null);            // IR from analyze
  const [mapping, setMapping] = useState(null);              // Mapping report from map
  const [materialized, setMaterialized] = useState(null);    // Pre-commit summary
  const [sourceJson, setSourceJson] = useState('');          // Source workflow JSON
  const [mappedJsonText, setMappedJsonText] = useState('');  // Editable Canvas JSON
  const [mappedDirty, setMappedDirty] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDecisions, setShowDecisions] = useState(false);
  const [committedNativeId, setCommittedNativeId] = useState(null);
  /* G466 — V2 confirm dialog state (replaces native window.confirm) */
  const [confirmDialog, setConfirmDialog] = useState(null);

  /* G468 — Resolve real source type per connector lookup (mirrors the
   * Workflows-tab fix). The Visual Mapper select-cards had the same
   * buggy `(w.connectorType || w.platform || 'temporal')` fallback that
   * mislabeled every Conductor workflow as "temporal". */
  const connectorById = useMemo(() => {
    const m = new Map();
    for (const c of (connectors || [])) {
      if (c?.id) m.set(c.id, c);
    }
    return m;
  }, [connectors]);
  const resolveSourceLabel = useCallback((w) => {
    if (!w) return 'unknown';
    if (w.connectorType) return String(w.connectorType).toLowerCase();
    if (w.connectorName) return String(w.connectorName).toLowerCase();
    const conn = w.connectionId ? connectorById.get(w.connectionId) : null;
    if (conn) {
      const t = conn.type || conn.connectorType || conn.kind || conn.name;
      if (t) return String(t).toLowerCase();
    }
    if (w.platform) return String(w.platform).toLowerCase();
    return 'unknown';
  }, [connectorById]);

  /* Workflow progress badge derivation (mirrors v1 STEP_LABELS). */
  const progressOf = (w) => {
    if (w?.nativeWorkflowId)            return 'COMMITTED';
    if (w?.mapperProgress === 'REVIEW_READY' || w?.mapperProgress === 'MATERIALIZED') return 'REVIEW_READY';
    if (w?.mapperProgress === 'ANALYZED' || w?.mapperProgress === 'MAPPED') return 'ANALYZED';
    return 'NOT_STARTED';
  };

  /* Unwrap helper for varied response shapes. */
  const unwrapData = (res) => res?.data ?? res;

  /* ── Step 0 → 1: Analyze ─────────────────────────────────────────── */
  const runAnalyze = async (wf) => {
    setBusy(true);
    try {
      const res = await api.discoveryMapperAnalyze?.(wf.id);
      const d = unwrapData(res);
      setAnalysis(d?.analysis || d || null);
      setStep(1);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Analyze failed', description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  /* ── Step 1 → 2: Map + Materialize + SourceJson (parallel) ───────── */
  const runMapAndMaterialize = async (wf) => {
    setBusy(true);
    try {
      const [mapRes, matRes, srcRes] = await Promise.all([
        api.discoveryMapperMap?.(wf.id),
        api.discoveryMapperMaterialize?.(wf.id),
        api.discoveryMapperSourceJson?.(wf.id),
      ]);
      const mapData = unwrapData(mapRes);
      const matData = unwrapData(matRes);
      const srcData = unwrapData(srcRes);
      setMapping(mapData?.mapping || mapData?.report || mapData || null);
      setMaterialized(matData?.materialized || matData || null);
      const src = typeof srcData === 'string' ? srcData : JSON.stringify(srcData, null, 2);
      setSourceJson(src);
      const canvas = matData?.canvasDefinition || matData?.canvas || matData;
      setMappedJsonText(typeof canvas === 'string' ? canvas : JSON.stringify(canvas, null, 2));
      setMappedDirty(false);
      setJsonError(null);
      setStep(2);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Mapping failed', description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  /* ── Step 0: Smart resume into the right step ────────────────────── */
  const handleSelectWorkflow = async (wf) => {
    setSelectedWf(wf);
    setCommittedNativeId(wf.nativeWorkflowId || null);
    const prog = progressOf(wf);
    if (prog === 'COMMITTED') { setStep(4); return; }
    if (prog === 'REVIEW_READY') {
      await runMapAndMaterialize(wf);
      return;
    }
    if (prog === 'ANALYZED') {
      // Resume on step 1 — fetch the cached analysis (analyze is idempotent server-side).
      await runAnalyze(wf);
      return;
    }
    // NOT_STARTED — kick off a fresh analyze.
    await runAnalyze(wf);
  };

  /* ── Step 2 textarea → keystroke JSON validation ─────────────────── */
  const onMappedChange = (txt) => {
    setMappedJsonText(txt);
    setMappedDirty(true);
    try { JSON.parse(txt || '{}'); setJsonError(null); }
    catch (e) { setJsonError('Invalid JSON: ' + e.message); }
  };

  /* ── Step 3: Commit ──────────────────────────────────────────────── */
  const handleCommit = async () => {
    if (!selectedWf) return;
    let body = null;
    if (mappedDirty) {
      try {
        const parsed = JSON.parse(mappedJsonText || '{}');
        body = { canvasDefinition: parsed };
      } catch (e) {
        addToast({ tone: 'danger', title: 'Cannot commit invalid JSON', description: e.message });
        return;
      }
    }
    setBusy(true);
    try {
      const res = await api.discoveryMapperCommit?.(selectedWf.id, body);
      const d = unwrapData(res);
      if (d?.success === false) {
        addToast({ tone: 'danger', title: 'Commit failed', description: d?.error || d?.message });
        return;
      }
      const nativeId = d?.nativeWorkflowId || d?.canvasWorkflowId;
      setCommittedNativeId(nativeId || null);
      addToast({ tone: 'success', title: 'Committed to Canvas',
        description: d?.alreadyConverted ? 'Already converted — reused existing CEG.' : `Native CEG ${nativeId?.slice(0, 8)}… ready.` });
      setStep(4);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Commit failed', description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  /* ── Reset (local + optional backend) ────────────────────────────── */
  const resetLocal = () => {
    setStep(0); setSelectedWf(null); setAnalysis(null); setMapping(null);
    setMaterialized(null); setSourceJson(''); setMappedJsonText('');
    setMappedDirty(false); setJsonError(null); setCommittedNativeId(null);
  };

  /* G466 — Reset & re-run: drives a FRESH conversion via the full mapper
   * pipeline. Pre-G466 the "Reset & re-convert" button only called
   * mapperReset (which clears local state on the backend) and then
   * dropped the user back at step 0 with no way to re-run. After the
   * reset clears state, the user clicked the SAME workflow card and
   * was instantly thrown back to step 4 ("Done") because the local
   * workflows array still reported nativeWorkflowId (stale until parent
   * reloads). Net: a button labelled "re-convert" that didn't drive
   * any re-conversion.
   *
   * The new flow:
   *   1. Optional confirm via V2 dialog (NOT window.confirm).
   *   2. Call mapperReset — backend clears nativeWorkflowId + sets
   *      mapperProgress back to NOT_STARTED on the discovery record.
   *   3. Call reload() so the parent's workflows[] is fresh — without
   *      this, progressOf still reports COMMITTED for this workflow
   *      and handleSelectWorkflow's smart-resume jumps to step 4.
   *   4. Stash the workflow id, clear local mapper state, and AUTO-
   *      DRIVE the full pipeline: analyze → map+materialize → commit.
   *      This is the actual "re-convert" the button name promised.
   *   5. Toast at each milestone so the operator knows what's running.
   */
  /* G466/G472 — Reset confirm panel — clears mapper state and RETURNS THE
   * USER TO THE SELECT STEP. No auto-run. Per the user's explicit direction:
   * the user drives every stage manually after a reset (Analyze → Map →
   * Commit), so they retain visibility/control over the side-by-side IR
   * detail, mapping decisions, JSON edits, and commit summary at each
   * stage. Earlier G470 auto-drive version stripped that interactivity
   * by running all stages back-to-back; reverted here.
   */
  const askResetConfirm = () => {
    if (!selectedWf) { resetLocal(); return; }
    setConfirmDialog({
      title: 'Reset mapper state for this workflow?',
      body: 'This clears the cached analysis, mapping, and canvas draft. The source workflow is untouched. After reset, click the workflow on the Select step to drive Analyze → Map → Commit again at your own pace — pulling the latest source on each stage.',
      destructive: false,
      confirmLabel: 'Reset',
      onConfirm: () => { setConfirmDialog(null); resetAndReturnToSelect(); },
    });
  };

  const resetAndReturnToSelect = async () => {
    if (!selectedWf) return;
    const wfId = selectedWf.id;
    setBusy(true);
    try {
      // Clear mapper state on the backend (detaches nativeWorkflowId,
      // resets mapperProgress to NOT_STARTED on the discovery record).
      await api.discoveryMapperReset?.(wfId);
      // Reload parent workflow list so progressOf reflects the fresh state
      // — without this, the workflow card would still claim COMMITTED.
      if (typeof reload === 'function') {
        try { await reload(); } catch { /* non-fatal — local state still valid */ }
      }
      addToast({ tone: 'info', title: 'Mapper state cleared',
        description: 'Click the workflow on the Select step to start a fresh Analyze → Map → Commit.' });
      // Drop the user back at step 0 so they pick the workflow and drive
      // each stage themselves. resetLocal also clears every local cache.
      resetLocal();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Reset failed', description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  // Legacy name kept for any internal callers; delegates to the V2 flow.
  const resetFull = askResetConfirm;

  /* ── Auto-convert (poll status) ─────────────────────────────────── */
  const autoConvert = async (wf) => {
    setBusy(true);
    try {
      const res = await api.discoveryMapperExecuteWorkflow?.(wf.id);
      const d = unwrapData(res);
      addToast({ tone: 'info', title: 'Auto-convert started',
        description: `Run ${(d?.runId || '').slice(0, 8)}… — polling for completion.` });
      // Poll once after 8s; v1 uses the same simple cadence.
      setTimeout(async () => {
        try {
          const s = await api.discoveryMapperStatus?.(wf.id);
          const sd = unwrapData(s);
          if (sd?.mapperProgress === 'COMMITTED' || sd?.nativeWorkflowId) {
            setSelectedWf({ ...wf, ...sd });
            setCommittedNativeId(sd.nativeWorkflowId);
            setStep(4);
            addToast({ tone: 'success', title: 'Auto-converted', description: 'Workflow is now a native CEG.' });
          } else {
            addToast({ tone: 'warning', title: 'Auto-convert still in flight',
              description: 'Check back in a moment — the task is queued.' });
          }
        } catch (e) { /* silent — backend can be slow */ }
      }, 8000);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Auto-convert failed', description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  /* ── Render: progress stepper + active step body ─────────────────── */
  return (
    <>
      <Card padding="none" className="mb-5">
        <div className="p-4">
          <h3 className="text-[14px] font-bold tracking-tight m-0 mb-1">Visual Mapper</h3>
          <p className="text-[12px] text-v2-ink-secondary">
            Walk an external workflow through the 5-stage conversion pipeline. The committed result is a native CEG
            that the platform fully owns and protects.
          </p>
        </div>
        {/* Step indicator strip */}
        <div className="border-t border-v2-border-subtle px-4 py-3 flex items-center gap-1">
          {MAPPER_STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button type="button" disabled={i > step || busy}
                onClick={() => { if (i < step) setStep(i); }}
                className={'flex items-center gap-2 text-[12px] font-semibold transition-colors ' +
                  (i === step ? 'text-v2-action-strong' : i < step ? 'text-v2-success cursor-pointer hover:underline' : 'text-v2-ink-muted')}>
                <span className={'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ' +
                  (i === step ? 'bg-v2-action text-white'
                   : i < step ? 'bg-v2-success text-white'
                   : 'bg-v2-muted text-v2-ink-muted')}>
                  {i < step ? '✓' : i + 1}
                </span>
                {s.label}
              </button>
              {i < MAPPER_STEPS.length - 1 && (
                <div className={'flex-1 h-px ' + (i < step ? 'bg-v2-success' : 'bg-v2-border-subtle')} />
              )}
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* ── Step 0: Select ─────────────────────────────────────────── */}
      {step === 0 && (
        <Card>
          <Card.Header title={`Select a workflow · ${workflows.length}`} />
          <Card.Body>
            {workflows.length === 0 ? (
              <EmptyState icon={<Workflow size={20} />} title="No workflows discovered"
                description="Run discovery on the Sources tab first to populate this list." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {workflows.map((w) => {
                  const prog = progressOf(w);
                  const tone = prog === 'COMMITTED' ? 'success'
                            : prog === 'REVIEW_READY' ? 'trust'
                            : prog === 'ANALYZED' ? 'warning' : 'muted';
                  const label = prog === 'COMMITTED' ? 'Committed'
                             : prog === 'REVIEW_READY' ? 'Review ready'
                             : prog === 'ANALYZED' ? 'Analyzed' : 'Not started';
                  return (
                    <button key={w.id} type="button"
                      onClick={() => handleSelectWorkflow(w)}
                      disabled={busy}
                      className="text-left border border-v2-border-subtle rounded-v2-md p-3 hover:border-v2-action hover:bg-v2-action-soft transition-colors v2-focus-ring disabled:opacity-50">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Pill tone={tone} size="sm" dot>{label}</Pill>
                        {prog === 'NOT_STARTED' && (
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); autoConvert(w); }}
                            className="ml-auto text-[10.5px] text-v2-action-strong hover:underline flex items-center gap-0.5">
                            ⚡ Auto
                          </button>
                        )}
                      </div>
                      <div className="text-[13px] font-semibold text-v2-ink truncate">{w.workflowType || w.name}</div>
                      {/* G468 — Resolve real source type via connector lookup
                       * instead of the legacy `(w.platform || 'temporal')`
                       * fallback that labelled every Conductor workflow as
                       * "temporal". The connector name is also shown so the
                       * operator can trace exactly which source feed this
                       * workflow came from. */}
                      <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate">
                        {resolveSourceLabel(w)} · {(w.temporalNamespace || w.namespace || 'default')}
                        {w.taskQueue && ` · ${w.taskQueue}`}
                        {(() => {
                          const conn = w.connectionId ? connectorById.get(w.connectionId) : null;
                          return conn?.name ? ` · ${conn.name}` : '';
                        })()}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ── Step 1: Analyze (IR Viewer) ────────────────────────────── */}
      {step === 1 && selectedWf && (
        <Card>
          <Card.Header title={`Analysis · ${selectedWf.workflowType || selectedWf.name}`} actions={
            <>
              <Button variant="ghost" size="sm" onClick={resetLocal}>Back</Button>
              <Button variant="primary" size="sm" iconLeft={<ChevronRight size={13} />}
                onClick={() => runMapAndMaterialize(selectedWf)} disabled={busy}>
                {busy ? 'Working…' : 'Proceed to mapping'}
              </Button>
            </>
          } />
          <Card.Body>
            {!analysis ? (
              <p className="text-[12.5px] text-v2-ink-muted">{busy ? 'Analyzing…' : 'No analysis result.'}</p>
            ) : (() => {
              /* G473 — Resolve the IR object once. The mapper API wraps the
                 actual IR inside an .ir field (V1 reads
                 `pipelineData.analysis.ir`). V2 was reading the fields one
                 level too shallow, so nodes/edges/controlBlocks/diagnostics
                 all came out as 0 even though the response carried them
                 under analysis.ir. Fall back to analysis itself for
                 forward compatibility if the backend ever flattens. */
              const ir = analysis?.ir || analysis || {};
              return (
              <>
                {/* G471 — Restored V1 Max Parallelism metric (was missing in V2). */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                  <MetricCard label="Nodes"          value={ir.nodes?.length || ir.nodeCount || 0} tone="cyan" />
                  <MetricCard label="Edges"          value={ir.edges?.length || ir.edgeCount || 0} tone="trust" />
                  <MetricCard label="Control blocks" value={ir.controlBlocks?.length || ir.controlBlockCount || 0} tone="action" />
                  <MetricCard label="Max parallelism" value={ir.maxParallelism || ir.parallelism || 1} tone="trust" />
                  <MetricCard label="Diagnostics"    value={ir.diagnostics?.length || 0}
                    tone={(ir.diagnostics?.length || 0) > 0 ? 'warning' : 'success'} />
                </div>
                {Array.isArray(ir.diagnostics) && ir.diagnostics.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-[12.5px] font-semibold text-v2-ink mb-2">Diagnostics ({ir.diagnostics.length})</h4>
                    <ul className="space-y-2">
                      {/* G471 — Rich diagnostic surface: severity-tinted background,
                          category field, suggestion copy, nodeId monospace link. */}
                      {ir.diagnostics.slice(0, 20).map((d, i) => {
                        const sev = (d.severity || 'INFO').toUpperCase();
                        const sevBg = sev === 'ERROR' ? 'bg-v2-danger-soft border-v2-danger'
                                    : sev === 'WARNING' || sev === 'WARN' ? 'bg-v2-warning-soft border-v2-warning'
                                    : 'bg-v2-action-soft border-v2-action';
                        return (
                          <li key={i} className={`border-l-4 ${sevBg} rounded-v2-sm px-3 py-2 text-[12px]`}>
                            <div className="flex items-center gap-2 mb-1">
                              <Pill tone={sev === 'ERROR' ? 'danger' : sev === 'WARNING' || sev === 'WARN' ? 'warning' : 'cyan'} size="sm">{sev}</Pill>
                              {d.category && <Pill tone="muted" size="sm">{d.category}</Pill>}
                              {d.nodeId && <span className="text-v2-ink-muted font-mono-v2 text-[10.5px] ml-auto">@ {d.nodeId}</span>}
                            </div>
                            <div className="text-v2-ink">{d.message || d.text || JSON.stringify(d)}</div>
                            {d.suggestion && (
                              <div className="text-v2-ink-secondary text-[11.5px] mt-1 italic">💡 {d.suggestion}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {/* G471 — Control Flow Blocks section (was completely missing in V2). */}
                {Array.isArray(ir.controlBlocks) && ir.controlBlocks.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-[12.5px] font-semibold text-v2-ink mb-2">Control flow blocks ({ir.controlBlocks.length})</h4>
                    <ul className="space-y-2">
                      {ir.controlBlocks.map((cb, i) => {
                        const bt = (cb.blockType || cb.type || 'block').toUpperCase();
                        const bgClass = bt === 'FORK' || bt === 'PARALLEL' ? 'bg-v2-trust-soft border-v2-trust'
                                      : bt === 'SWITCH' || bt === 'DECISION' ? 'bg-v2-action-soft border-v2-action'
                                      : bt === 'LOOP' || bt === 'DO_WHILE' ? 'bg-v2-warning-soft border-v2-warning'
                                      : 'bg-v2-surface border-v2-border-subtle';
                        return (
                          <li key={i} className={`border ${bgClass} rounded-v2-md px-3 py-2 text-[12px]`}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Pill tone={bt === 'FORK' ? 'trust' : bt === 'SWITCH' ? 'action' : bt === 'LOOP' ? 'warning' : 'muted'} size="sm">{bt}</Pill>
                              <span className="font-mono-v2 text-[11.5px] text-v2-ink">{cb.entryNodeRef || cb.entryRef || cb.id}</span>
                              {cb.exitNodeRef && (
                                <>
                                  <span className="text-v2-ink-muted">→</span>
                                  <span className="font-mono-v2 text-[11.5px] text-v2-ink-secondary">{cb.exitNodeRef}</span>
                                </>
                              )}
                            </div>
                            {cb.branches && typeof cb.branches === 'object' && Object.keys(cb.branches).length > 0 && (
                              <div className="ml-2 mt-1 space-y-0.5">
                                {Object.entries(cb.branches).map(([name, nodes]) => (
                                  <div key={name} className="text-[11px] font-mono-v2 text-v2-ink-secondary">
                                    <span className="text-v2-action-strong font-semibold">{name}</span>
                                    {Array.isArray(nodes) && nodes.length > 0 && (
                                      <span className="ml-1">→ [{nodes.join(', ')}]</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {/* G471 — Node list with full drill-in (Ref/Layer/Lane/Dependencies/
                    DataBindings/Config). Pre-G471 V2 only showed type · id.  */}
                {Array.isArray(ir.nodes) && ir.nodes.length > 0 && (
                  <details className="mb-2" open>
                    <summary className="cursor-pointer text-[12.5px] font-semibold text-v2-ink mb-2">IR nodes ({ir.nodes.length}) — click to expand individual nodes</summary>
                    <ul className="mt-2 space-y-1.5 max-h-[400px] overflow-auto">
                      {ir.nodes.map((n, i) => {
                        const nid = n.id || n.nodeRef || n.taskReferenceName || n.name || `node_${i}`;
                        const ntype = n.type || n.kind || n.canonicalType || '?';
                        const hasDetail = (n.layer != null) || (n.lane != null)
                          || (Array.isArray(n.dependencies) && n.dependencies.length > 0)
                          || (Array.isArray(n.dataBindings) && n.dataBindings.length > 0)
                          || (n.config && Object.keys(n.config).length > 0);
                        return (
                          <li key={nid + i} className="border border-v2-border-subtle rounded-v2-sm bg-v2-surface">
                            {hasDetail ? (
                              <details>
                                <summary className="cursor-pointer px-2.5 py-1.5 text-[11.5px] flex items-center gap-2 hover:bg-v2-action-soft">
                                  <Pill tone="action" size="sm">{ntype}</Pill>
                                  <span className="font-mono-v2 text-v2-ink">{nid}</span>
                                  {n.label && n.label !== nid && <span className="text-v2-ink-muted">· {n.label}</span>}
                                </summary>
                                <div className="px-2.5 pb-2 text-[11px] space-y-1 border-t border-v2-border-subtle pt-1.5">
                                  {(n.layer != null) && <div><span className="text-v2-ink-muted">Layer:</span> <span className="font-mono-v2">{n.layer}</span></div>}
                                  {(n.lane != null) && <div><span className="text-v2-ink-muted">Lane:</span> <span className="font-mono-v2">{n.lane}</span></div>}
                                  {Array.isArray(n.dependencies) && n.dependencies.length > 0 && (
                                    <div>
                                      <span className="text-v2-ink-muted">Dependencies:</span>
                                      <div className="flex flex-wrap gap-1 mt-0.5">
                                        {n.dependencies.map((dep, di) => <Pill key={di} tone="muted" size="sm">{dep}</Pill>)}
                                      </div>
                                    </div>
                                  )}
                                  {Array.isArray(n.dataBindings) && n.dataBindings.length > 0 && (
                                    <div>
                                      <span className="text-v2-ink-muted">Data bindings:</span>
                                      <ul className="ml-2 mt-0.5">
                                        {n.dataBindings.map((db, dbi) => (
                                          <li key={dbi} className="font-mono-v2 text-[10.5px]">
                                            <span className="text-v2-action-strong">{db.targetInputKey || db.target || '?'}</span>
                                            {' ← '}
                                            <span className="text-v2-ink-secondary">{db.sourceNodeRef ? `${db.sourceNodeRef}.${db.sourceField || ''}` : db.rawExpression || db.expression || '?'}</span>
                                            {db.resolved === false && <span className="ml-1 text-v2-danger">(unresolved)</span>}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {n.config && typeof n.config === 'object' && Object.keys(n.config).length > 0 && (
                                    <details>
                                      <summary className="cursor-pointer text-v2-ink-muted">Config ({Object.keys(n.config).length} keys)</summary>
                                      <ul className="ml-2 mt-0.5">
                                        {Object.entries(n.config).slice(0, 12).map(([k, v]) => (
                                          <li key={k} className="font-mono-v2 text-[10.5px]">
                                            <span className="text-v2-action-strong">{k}</span>: <span className="text-v2-ink-secondary truncate">{typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 60)}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  )}
                                </div>
                              </details>
                            ) : (
                              <div className="px-2.5 py-1.5 text-[11.5px] flex items-center gap-2">
                                <Pill tone="action" size="sm">{ntype}</Pill>
                                <span className="font-mono-v2 text-v2-ink">{nid}</span>
                                {n.label && n.label !== nid && <span className="text-v2-ink-muted">· {n.label}</span>}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
              </>
              );
            })()}
          </Card.Body>
        </Card>
      )}

      {/* ── Step 2: Map (two-panel JSON review) ────────────────────── */}
      {step === 2 && selectedWf && (
        <Card>
          <Card.Header title={`Map · ${selectedWf.workflowType || selectedWf.name}`} actions={
            <>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
              <Button variant="primary" size="sm" iconLeft={<ChevronRight size={13} />}
                onClick={() => setStep(3)} disabled={!!jsonError || busy}>
                Proceed to commit
              </Button>
            </>
          } />
          <Card.Body>
            {(() => {
              /* G474 — Resolve manifest the same way V1 does:
                 `pipelineData?.mapping?.manifest || pipelineData?.materialized?.manifest`.
                 The mapper API returns `{ ..., manifest: { nodeMappings: [...],
                 connectionMappings: [...], dataFlowRewrites: [...], warnings: [...] } }`.
                 Pre-G474 V2 was reading `mapping.nodeMappings` directly — one
                 level too shallow, same root-cause as G473 (analysis vs
                 analysis.ir). Per V1_V2_REDESIGN_GROUND_RULES.md Rule 4:
                 align V2 to V1's exact field path before doing any other
                 refactor. */
              // eslint-disable-next-line no-unused-vars
              const manifest = mapping?.manifest || materialized?.manifest || mapping || materialized || {};
              return (
              <>
            {jsonError && (
              <div className="border-l-4 border-v2-danger bg-v2-danger-soft px-3 py-2 mb-3 rounded-v2-sm text-[12px] text-v2-danger">
                <strong>JSON error:</strong> {jsonError}
              </div>
            )}
            {mappedDirty && !jsonError && (
              <div className="border-l-4 border-v2-warning bg-v2-warning-soft px-3 py-2 mb-3 rounded-v2-sm text-[12px]">
                You've edited the generated Canvas JSON — your version will be committed.
              </div>
            )}
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Source workflow JSON</label>
                  <span className="text-[10.5px] text-v2-ink-muted">{sourceJson.length.toLocaleString()} chars</span>
                </div>
                <textarea readOnly value={sourceJson}
                  className="text-[11px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full bg-v2-muted"
                  style={{ minHeight: '460px' }} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                    Mapped Canvas JSON {mappedDirty && <span className="text-v2-warning ml-1">· edited</span>}
                  </label>
                  <Button variant="ghost" size="sm" onClick={() => { setMappedDirty(false); setJsonError(null); runMapAndMaterialize(selectedWf); }} disabled={!mappedDirty || busy}>
                    Reset edits
                  </Button>
                </div>
                <textarea value={mappedJsonText}
                  onChange={(e) => onMappedChange(e.target.value)}
                  className="text-[11px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full"
                  style={{ minHeight: '460px' }} />
              </div>
            </div>
            {mapping && (
              <div className="mt-4 border-t border-v2-border-subtle pt-3 space-y-4">
                {/* G471 — Warnings preview with severity badges + suggestions
                    (was missing in V2 — V1 surfaced these in the Map step too). */}
                {Array.isArray(manifest.warnings) && manifest.warnings.length > 0 && (
                  <div>
                    <h4 className="text-[12.5px] font-semibold text-v2-ink mb-2">Warnings ({manifest.warnings.length})</h4>
                    <ul className="space-y-1.5">
                      {manifest.warnings.slice(0, 12).map((w, i) => {
                        const sev = String(w.severity || w.level || 'WARN').toUpperCase();
                        const sevBg = sev === 'ERROR' ? 'border-v2-danger bg-v2-danger-soft'
                                    : sev === 'INFO' ? 'border-v2-action bg-v2-action-soft'
                                    : 'border-v2-warning bg-v2-warning-soft';
                        return (
                          <li key={i} className={`border-l-4 ${sevBg} rounded-v2-sm px-3 py-1.5 text-[11.5px]`}>
                            <div className="flex items-center gap-2 mb-0.5">
                              <Pill tone={sev === 'ERROR' ? 'danger' : sev === 'INFO' ? 'cyan' : 'warning'} size="sm">{sev}</Pill>
                              {w.category && <Pill tone="muted" size="sm">{w.category}</Pill>}
                              {w.nodeRef && <span className="text-v2-ink-muted font-mono-v2 text-[10.5px] ml-auto">@ {w.nodeRef}</span>}
                            </div>
                            <div className="text-v2-ink">{w.message || (typeof w === 'string' ? w : JSON.stringify(w))}</div>
                            {w.suggestion && <div className="text-v2-ink-secondary italic mt-0.5">💡 {w.suggestion}</div>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {/* G471 — Node Mappings TABLE (6 columns) with drill-in to per-node
                    rationale + rebuild reason + translated config. Pre-G471 V2 had
                    only a flat list of pills — no traceability, no override visibility. */}
                <div>
                  <button type="button" onClick={() => setShowDecisions(!showDecisions)}
                    className="text-[12.5px] font-semibold text-v2-ink hover:text-v2-action">
                    {showDecisions ? '▼' : '▶'} Mapping decisions ({(manifest.nodeMappings?.length || 0)} node(s))
                  </button>
                  {showDecisions && Array.isArray(manifest.nodeMappings) && manifest.nodeMappings.length > 0 && (
                    <div className="mt-2 border border-v2-border-subtle rounded-v2-md overflow-hidden">
                      <table className="w-full text-[11.5px]">
                        <thead className="bg-v2-surface-alt border-b border-v2-border-subtle">
                          <tr className="text-left">
                            <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Source node</th>
                            <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Source type</th>
                            <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">→</th>
                            <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Canvas type</th>
                            <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Confidence</th>
                            <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manifest.nodeMappings.map((nm, i) => {
                            const conf = nm.confidence;
                            const confLabel = conf === 'HIGH' || conf > 0.8 ? 'HIGH'
                                            : conf === 'MEDIUM' || (conf > 0.5 && conf <= 0.8) ? 'MEDIUM'
                                            : conf === 'LOW' || conf <= 0.5 ? 'LOW' : (conf || '—');
                            const confTone = confLabel === 'HIGH' ? 'success'
                                           : confLabel === 'MEDIUM' ? 'warning' : 'danger';
                            const status = nm.rebuildRequired ? 'REBUILD' : 'READY';
                            const hasDetail = nm.rationale || nm.rebuildReason
                              || (nm.translatedConfig && Object.keys(nm.translatedConfig).length > 0)
                              || (Array.isArray(nm.untranslatedKeys) && nm.untranslatedKeys.length > 0);
                            return (
                              <React.Fragment key={i}>
                                <tr className="border-t border-v2-border-subtle hover:bg-v2-action-soft">
                                  <td className="px-2 py-1.5 font-mono-v2">
                                    {hasDetail ? (
                                      <details>
                                        <summary className="cursor-pointer flex items-center gap-1">
                                          <ChevronRight size={11} />
                                          <span>{nm.sourceLabel || nm.sourceNodeRef || nm.sourceRef || '—'}</span>
                                        </summary>
                                        <div className="text-[11px] pl-3 pt-1 space-y-1 font-sans-v2 normal-case">
                                          {nm.rationale && (
                                            <div><span className="text-v2-ink-muted">Rationale:</span> {nm.rationale}</div>
                                          )}
                                          {nm.rebuildReason && (
                                            <div className="text-v2-danger"><strong>Rebuild reason:</strong> {nm.rebuildReason}</div>
                                          )}
                                          {nm.translatedConfig && Object.keys(nm.translatedConfig).length > 0 && (
                                            <details>
                                              <summary className="cursor-pointer text-v2-ink-muted">Translated config ({Object.keys(nm.translatedConfig).length} keys)</summary>
                                              <ul className="ml-2 mt-0.5">
                                                {Object.entries(nm.translatedConfig).slice(0, 10).map(([k, v]) => (
                                                  <li key={k} className="font-mono-v2 text-[10.5px]">
                                                    <span className="text-v2-action-strong">{k}</span>: <span className="text-v2-ink-secondary">{typeof v === 'object' ? JSON.stringify(v) : String(v).slice(0, 60)}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                            </details>
                                          )}
                                          {Array.isArray(nm.untranslatedKeys) && nm.untranslatedKeys.length > 0 && (
                                            <div className="text-v2-warning">
                                              <strong>Untranslated keys:</strong> {nm.untranslatedKeys.join(', ')}
                                            </div>
                                          )}
                                        </div>
                                      </details>
                                    ) : (
                                      <span>{nm.sourceLabel || nm.sourceNodeRef || nm.sourceRef || '—'}</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <Pill tone="action" size="sm">{nm.sourceType || nm.sourceNodeType || '?'}</Pill>
                                  </td>
                                  <td className="px-2 py-1.5 text-v2-ink-muted">→</td>
                                  <td className="px-2 py-1.5">
                                    <Pill tone="trust" size="sm">{nm.targetType || nm.canvasNodeType || '?'}</Pill>
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <Pill tone={confTone} size="sm">{confLabel}</Pill>
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <Pill tone={status === 'REBUILD' ? 'danger' : 'success'} size="sm">{status}</Pill>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {/* G471 — Data Flow Rewrites section (was completely missing in V2).
                    Shows every ${taskRef.output.field} expression that the engine
                    rewrote to a Canvas expression, with resolved/unresolved flag. */}
                {Array.isArray(manifest.dataFlowRewrites) && manifest.dataFlowRewrites.length > 0 && (
                  <div>
                    <h4 className="text-[12.5px] font-semibold text-v2-ink mb-2">Data flow rewrites ({manifest.dataFlowRewrites.length})</h4>
                    <div className="border border-v2-border-subtle rounded-v2-md overflow-hidden">
                      <table className="w-full text-[11px]">
                        <thead className="bg-v2-surface-alt border-b border-v2-border-subtle">
                          <tr className="text-left">
                            <th className="px-2 py-1 font-semibold text-v2-ink-muted">Original</th>
                            <th className="px-2 py-1 font-semibold text-v2-ink-muted">→</th>
                            <th className="px-2 py-1 font-semibold text-v2-ink-muted">Canvas</th>
                            <th className="px-2 py-1 font-semibold text-v2-ink-muted">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manifest.dataFlowRewrites.slice(0, 30).map((rw, i) => (
                            <tr key={i} className="border-t border-v2-border-subtle">
                              <td className="px-2 py-1 font-mono-v2 text-[10.5px] text-v2-ink">{rw.rawExpression || rw.originalExpression || rw.from || '—'}</td>
                              <td className="px-2 py-1 text-v2-ink-muted">→</td>
                              <td className="px-2 py-1 font-mono-v2 text-[10.5px]">
                                {rw.resolved
                                  ? <span className="text-v2-success-strong">{rw.canvasExpression || rw.to || '—'}</span>
                                  : <span className="text-v2-danger italic">{rw.unresolvedReason || 'unresolved'}</span>}
                              </td>
                              <td className="px-2 py-1">
                                <Pill tone={rw.resolved ? 'success' : 'danger'} size="sm">
                                  {rw.resolved ? 'resolved' : 'unresolved'}
                                </Pill>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
              </>
              );
            })()}
          </Card.Body>
        </Card>
      )}

      {/* ── Step 3: Commit ─────────────────────────────────────────── */}
      {step === 3 && selectedWf && (
        <Card>
          <Card.Header title={`Commit · ${selectedWf.workflowType || selectedWf.name}`} actions={
            <>
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>Back</Button>
              <Button variant="primary" size="sm" iconLeft={<ShieldCheck size={13} />}
                onClick={handleCommit} disabled={busy}>
                {busy ? 'Committing…' : 'Commit & open in Canvas'}
              </Button>
            </>
          } />
          <Card.Body>
            {(() => {
              /* G474 — Same manifest resolution as Stage 3. The Commit
                 step reads node mappings, warnings, and data-flow rewrites
                 from the same manifest carried over from /mapper/map. The
                 analysis IR is still on the .ir path (G473). */
              const manifest = mapping?.manifest || materialized?.manifest || mapping || materialized || {};
              const ir = analysis?.ir || analysis || {};
              return (
              <>
            <p className="text-[12.5px] text-v2-ink-secondary mb-4">
              Materializes the Canvas JSON as a native CEG in your tenant. The workflow status moves to <strong>MAPPED</strong>.
              {mappedDirty && <span className="block mt-1 text-v2-warning">⚠ You edited the generated mapping — your version will be committed.</span>}
            </p>
            {/* G471 — V1-parity 6-metric grid (was 4 in V2): added High Confidence +
                Rebuild Required cards so operators see at a glance whether the
                conversion has any uncertainty before they commit. */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <MetricCard label="Source nodes" value={ir.nodes?.length || ir.nodeCount || 0} tone="cyan" />
              <MetricCard label="Canvas nodes" value={materialized?.canvasNodeCount || manifest.nodeMappings?.length || 0} tone="trust" />
              <MetricCard label="Connections" value={materialized?.connectionCount || manifest.connectionMappings?.length || 0} tone="action" />
              <MetricCard
                label="High confidence"
                value={(manifest.nodeMappings || []).filter((nm) => nm.confidence === 'HIGH' || nm.confidence > 0.8).length}
                tone="success"
              />
              <MetricCard
                label="Rebuild required"
                value={(manifest.nodeMappings || []).filter((nm) => nm.rebuildRequired).length}
                tone={(manifest.nodeMappings || []).filter((nm) => nm.rebuildRequired).length > 0 ? 'danger' : 'muted'}
              />
              <MetricCard label="Warnings" value={manifest.warnings?.length || 0}
                tone={(manifest.warnings?.length || 0) > 0 ? 'warning' : 'success'} />
            </div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {selectedWf.dataClassification && <Pill tone="trust" size="sm">{selectedWf.dataClassification}</Pill>}
              {(selectedWf.riskTier || selectedWf.risk) && (
                <Pill tone={RISK_TONE[(selectedWf.riskTier || selectedWf.risk || 'low').toLowerCase()]} size="sm">
                  risk: {(selectedWf.riskTier || selectedWf.risk || 'low').toLowerCase()}
                </Pill>
              )}
              {mappedDirty && <Pill tone="warning" size="sm">user-edited</Pill>}
            </div>
            {/* G471 — Warnings with proper severity badges + suggestions
                (V1 had these; V2 was rendering a flat <ul> with no severity
                tone and no suggestion copy). */}
            {Array.isArray(manifest.warnings) && manifest.warnings.length > 0 && (
              <div className="mb-4">
                <h4 className="text-[12.5px] font-semibold text-v2-ink mb-2">Warnings ({manifest.warnings.length})</h4>
                <ul className="space-y-1.5">
                  {manifest.warnings.slice(0, 12).map((w, i) => {
                    const sev = String(w.severity || w.level || 'WARN').toUpperCase();
                    const sevBg = sev === 'ERROR' ? 'border-v2-danger bg-v2-danger-soft'
                                : sev === 'INFO' ? 'border-v2-action bg-v2-action-soft'
                                : 'border-v2-warning bg-v2-warning-soft';
                    return (
                      <li key={i} className={`border-l-4 ${sevBg} rounded-v2-sm px-3 py-1.5 text-[11.5px]`}>
                        <div className="flex items-center gap-2 mb-0.5">
                          <Pill tone={sev === 'ERROR' ? 'danger' : sev === 'INFO' ? 'cyan' : 'warning'} size="sm">{sev}</Pill>
                          {w.category && <Pill tone="muted" size="sm">{w.category}</Pill>}
                          {w.nodeRef && <span className="text-v2-ink-muted font-mono-v2 text-[10.5px] ml-auto">@ {w.nodeRef}</span>}
                        </div>
                        <div className="text-v2-ink">{w.message || (typeof w === 'string' ? w : JSON.stringify(w))}</div>
                        {w.suggestion && <div className="text-v2-ink-secondary italic mt-0.5">💡 {w.suggestion}</div>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {/* G471 — Data Flow Rewrites recap (was missing in V2 commit step). */}
            {Array.isArray(manifest.dataFlowRewrites) && manifest.dataFlowRewrites.length > 0 && (
              <details className="mb-4">
                <summary className="cursor-pointer text-[12.5px] font-semibold text-v2-ink">Data flow rewrites ({manifest.dataFlowRewrites.length})</summary>
                <div className="mt-2 border border-v2-border-subtle rounded-v2-md overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-v2-surface-alt border-b border-v2-border-subtle">
                      <tr className="text-left">
                        <th className="px-2 py-1 font-semibold text-v2-ink-muted">Original</th>
                        <th className="px-2 py-1 font-semibold text-v2-ink-muted">→</th>
                        <th className="px-2 py-1 font-semibold text-v2-ink-muted">Canvas</th>
                        <th className="px-2 py-1 font-semibold text-v2-ink-muted">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifest.dataFlowRewrites.slice(0, 30).map((rw, i) => (
                        <tr key={i} className="border-t border-v2-border-subtle">
                          <td className="px-2 py-1 font-mono-v2 text-[10.5px] text-v2-ink">{rw.rawExpression || rw.originalExpression || rw.from || '—'}</td>
                          <td className="px-2 py-1 text-v2-ink-muted">→</td>
                          <td className="px-2 py-1 font-mono-v2 text-[10.5px]">
                            {rw.resolved
                              ? <span className="text-v2-success-strong">{rw.canvasExpression || rw.to || '—'}</span>
                              : <span className="text-v2-danger italic">{rw.unresolvedReason || 'unresolved'}</span>}
                          </td>
                          <td className="px-2 py-1">
                            <Pill tone={rw.resolved ? 'success' : 'danger'} size="sm">
                              {rw.resolved ? 'resolved' : 'unresolved'}
                            </Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
            {/* G471 — Final mapping decisions in V1-parity 6-column table format
                with per-row drill-in (rationale / rebuild reason / translated
                config / untranslated keys). V2 was rendering a flat row of
                pills with no traceability. */}
            {Array.isArray(manifest.nodeMappings) && manifest.nodeMappings.length > 0 && (
              <details open className="mt-2">
                <summary className="cursor-pointer text-[12.5px] font-semibold text-v2-ink">Final mapping decisions ({manifest.nodeMappings.length} node(s))</summary>
                <div className="mt-2 border border-v2-border-subtle rounded-v2-md overflow-hidden max-h-[420px] overflow-y-auto">
                  <table className="w-full text-[11.5px]">
                    <thead className="bg-v2-surface-alt border-b border-v2-border-subtle sticky top-0">
                      <tr className="text-left">
                        <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Source node</th>
                        <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Source type</th>
                        <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">→</th>
                        <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Canvas type</th>
                        <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Confidence</th>
                        <th className="px-2 py-1.5 font-semibold text-v2-ink-muted">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifest.nodeMappings.map((nm, i) => {
                        const conf = nm.confidence;
                        const confLabel = conf === 'HIGH' || conf > 0.8 ? 'HIGH'
                                        : conf === 'MEDIUM' || (conf > 0.5 && conf <= 0.8) ? 'MEDIUM'
                                        : conf === 'LOW' || conf <= 0.5 ? 'LOW' : (conf || '—');
                        const confTone = confLabel === 'HIGH' ? 'success'
                                       : confLabel === 'MEDIUM' ? 'warning' : 'danger';
                        const status = nm.rebuildRequired ? 'REBUILD' : 'READY';
                        const hasDetail = nm.rationale || nm.rebuildReason
                          || (nm.translatedConfig && Object.keys(nm.translatedConfig).length > 0)
                          || (Array.isArray(nm.untranslatedKeys) && nm.untranslatedKeys.length > 0);
                        return (
                          <tr key={i} className="border-t border-v2-border-subtle hover:bg-v2-action-soft">
                            <td className="px-2 py-1.5 font-mono-v2">
                              {hasDetail ? (
                                <details>
                                  <summary className="cursor-pointer flex items-center gap-1">
                                    <ChevronRight size={11} />
                                    <span>{nm.sourceLabel || nm.sourceNodeRef || nm.sourceRef || '—'}</span>
                                  </summary>
                                  <div className="text-[11px] pl-3 pt-1 space-y-1 font-sans-v2 normal-case">
                                    {nm.rationale && (
                                      <div><span className="text-v2-ink-muted">Rationale:</span> {nm.rationale}</div>
                                    )}
                                    {nm.rebuildReason && (
                                      <div className="text-v2-danger"><strong>Rebuild reason:</strong> {nm.rebuildReason}</div>
                                    )}
                                    {nm.translatedConfig && Object.keys(nm.translatedConfig).length > 0 && (
                                      <details>
                                        <summary className="cursor-pointer text-v2-ink-muted">Translated config ({Object.keys(nm.translatedConfig).length} keys)</summary>
                                        <ul className="ml-2 mt-0.5">
                                          {Object.entries(nm.translatedConfig).slice(0, 10).map(([k, v]) => (
                                            <li key={k} className="font-mono-v2 text-[10.5px]">
                                              <span className="text-v2-action-strong">{k}</span>: <span className="text-v2-ink-secondary">{typeof v === 'object' ? JSON.stringify(v) : String(v).slice(0, 60)}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                    {Array.isArray(nm.untranslatedKeys) && nm.untranslatedKeys.length > 0 && (
                                      <div className="text-v2-warning">
                                        <strong>Untranslated keys:</strong> {nm.untranslatedKeys.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                </details>
                              ) : (
                                <span>{nm.sourceLabel || nm.sourceNodeRef || nm.sourceRef || '—'}</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              <Pill tone="action" size="sm">{nm.sourceType || nm.sourceNodeType || '?'}</Pill>
                            </td>
                            <td className="px-2 py-1.5 text-v2-ink-muted">→</td>
                            <td className="px-2 py-1.5">
                              <Pill tone="trust" size="sm">{nm.targetType || nm.canvasNodeType || '?'}</Pill>
                            </td>
                            <td className="px-2 py-1.5">
                              <Pill tone={confTone} size="sm">{confLabel}</Pill>
                            </td>
                            <td className="px-2 py-1.5">
                              <Pill tone={status === 'REBUILD' ? 'danger' : 'success'} size="sm">{status}</Pill>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
              </>
              );
            })()}
          </Card.Body>
        </Card>
      )}

      {/* ── Step 4: Done ───────────────────────────────────────────── */}
      {step === 4 && selectedWf && (
        <Card>
          <Card.Body>
            <div className="border-l-4 border-v2-success bg-v2-success-soft rounded-v2-md px-4 py-3 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-v2-success-soft border border-v2-success flex items-center justify-center">
                <ShieldCheck size={20} className="text-v2-success" />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-bold text-v2-ink">Converted to native CEG</div>
                <div className="text-[12px] text-v2-ink-secondary">
                  {selectedWf.workflowType || selectedWf.name} is now a Canvas-managed workflow under full platform governance.
                </div>
                {committedNativeId && (
                  <div className="text-[11px] text-v2-ink-muted font-mono-v2 mt-0.5">
                    Native CEG id: {committedNativeId}
                  </div>
                )}
              </div>
            </div>
            {/* G466/G471 — Inline confirmation panel (no backdrop Modal).
                When user clicks "Reset & re-convert", instead of throwing
                a Modal-with-overlay, we expand an in-context confirmation
                panel right below the action buttons. This is the canonical
                v2 pattern for destructive in-place actions: keep the user
                grounded in the page, no focus-trap dance. */}
            {!confirmDialog && (
              <div className="flex items-center gap-2">
                {committedNativeId && (
                  <Button variant="primary" iconLeft={<FileText size={13} />}
                    onClick={() => navigate(`/canvas?workflowId=${committedNativeId}`)}>Open in Canvas</Button>
                )}
                <Button variant="secondary" iconLeft={<RotateCw size={13} />}
                  onClick={resetFull} disabled={busy}>
                  {busy ? 'Re-converting…' : 'Reset & re-convert'}
                </Button>
                <Button variant="ghost" onClick={resetLocal}>Back to list</Button>
              </div>
            )}
            {confirmDialog && (
              <div className="border border-v2-warning bg-v2-warning-soft rounded-v2-md p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-v2-warning border border-v2-warning flex items-center justify-center flex-shrink-0">
                    <RotateCw size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-v2-ink mb-1">{confirmDialog.title}</div>
                    <p className="text-[12px] text-v2-ink-secondary leading-relaxed mb-3">{confirmDialog.body}</p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={confirmDialog.destructive ? 'danger' : 'primary'}
                        size="sm"
                        iconLeft={<RotateCw size={13} />}
                        onClick={confirmDialog.onConfirm}
                        disabled={busy}
                      >
                        {busy ? 'Working…' : (confirmDialog.confirmLabel || 'Confirm')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDialog(null)} disabled={busy}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Tab 5: Audit
 * ────────────────────────────────────────────────────────────────── */

function AuditTab({ bindings, auditDash, addToast, reload, loading }) {
  const [repairing, setRepairing] = useState({});

  const kpis = useMemo(() => ({
    totalBindings: bindings.length,
    chainIntact:   bindings.filter(b => b.chainIntact !== false).length,
    chainBroken:   bindings.filter(b => b.chainIntact === false).length,
    totalDecisions: auditDash?.totalDecisions || auditDash?.decisionsLast24h || 0,
  }), [bindings, auditDash]);

  const repair = async (b) => {
    setRepairing(r => ({ ...r, [b.id]: true }));
    try {
      await api.discoveryRepairAuditChain?.(b.id);
      addToast({ tone: 'success', title: 'Chain repaired' });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Repair failed', description: err?.message });
    } finally {
      setRepairing(r => ({ ...r, [b.id]: false }));
    }
  };

  const repairAll = async () => {
    try {
      await api.discoveryRepairAllAuditChains?.();
      addToast({ tone: 'success', title: 'Bulk repair complete' });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Bulk repair failed', description: err?.message });
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total bindings" value={kpis.totalBindings} tone="cyan" />
        <MetricCard label="Chain intact"   value={kpis.chainIntact} tone="success" />
        <MetricCard label="Chain broken"   value={kpis.chainBroken}
          tone={kpis.chainBroken > 0 ? 'danger' : 'success'} />
        <MetricCard label="Decisions / 24h" value={(kpis.totalDecisions || 0).toLocaleString()} tone="trust" />
      </div>

      {kpis.chainBroken > 0 && (
        <div className="border-l-4 border-v2-danger bg-v2-danger-soft px-4 py-3 mb-4 rounded-v2-md flex items-center gap-3">
          <ShieldAlert size={18} className="text-v2-danger" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-v2-ink">{kpis.chainBroken} binding(s) have a broken audit hash chain.</div>
            <div className="text-[11.5px] text-v2-ink-secondary">A repair walks the chain forward from the last verified link and re-signs subsequent entries with a continuity marker.</div>
          </div>
          <Button variant="danger" size="sm" onClick={repairAll}>Repair all</Button>
        </div>
      )}

      <Card>
        <Card.Header title={`Bindings · ${bindings.length}`} />
        <Card.Body>
          {bindings.length === 0 ? (
            <EmptyState icon={<Shield size={20} />} title="No bindings yet"
              description={loading ? 'Loading…' : 'Protect a workflow on the Workflows tab to create its first GEL binding.'} />
          ) : (
            <Table
              ariaLabel="GEL bindings"
              density="compact"
              columns={[
                { key: 'workflow', header: 'Workflow', render: (b) => (
                  /* RC-25 / R1 — Workflow cell uses EntityLabel for the
                     "{name} ({shortid})" composition. Previously rendered
                     the binding's own id with a 12-char shortid (non-standard
                     length); EntityLabel normalises to the 8-char short-id
                     used everywhere else, and full UUID is on hover. */
                  <div>
                    <EntityLabel
                      kind="workflow"
                      id={b.id || b.discoveredWorkflowId}
                      name={b.workflowName || b.workflowType}
                      size="md"
                    />
                  </div>
                )},
                { key: 'connector', header: 'Source', render: (b) => (
                  <Pill tone="muted" size="sm">{b.connectionName || b.connectorType || '—'}</Pill>
                )},
                { key: 'mode', header: 'Enforcement', render: (b) => (
                  <Pill tone={ENF_MODE_TONE[b.enforcementMode] || 'muted'} size="sm">{b.enforcementMode || 'MONITOR'}</Pill>
                )},
                { key: 'chain', header: 'Hash chain', render: (b) => (
                  b.chainIntact === false
                    ? <Pill tone="danger" size="sm" dot>broken</Pill>
                    : <Pill tone="success" size="sm" dot>intact</Pill>
                )},
                { key: 'decisions', header: 'Decisions', align: 'right', render: (b) => (
                  <span className="font-mono-v2 text-[12px]">{(b.totalDecisions || 0).toLocaleString()}</span>
                )},
                { key: 'actions', header: '', align: 'right', render: (b) => (
                  b.chainIntact === false ? (
                    <Button variant="ghost" size="sm" className="!text-v2-danger" onClick={() => repair(b)} disabled={repairing[b.id]}>
                      {repairing[b.id] ? 'Repairing…' : 'Repair'}
                    </Button>
                  ) : null
                )},
              ]}
              rows={bindings}
              rowKey={(b) => b.id}
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Resolver — legacy escape hatch + feature flag
 * ────────────────────────────────────────────────────────────────── */

export default function ResolvedWorkflowDiscoveryPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthDiscoveryEnabled()) return <WorkflowDiscoveryPage {...props} />;
  return <WorkflowDiscoveryPageV2Inner {...props} />;
}

export { WorkflowDiscoveryPageV2Inner };
